import { useEffect, useState } from 'react'
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom'
import ResellerShell from './ResellerShell'
import MapPicker from '../../components/MapPicker'
import ActivationChecklist from '../../components/ActivationChecklist'
import Toggle from '../../components/Toggle'
import ProviderBadge from '../../components/ProviderBadge'
import { PreviewWidget } from '../setup/BrandingSetup'
import { tournamentApi, portalApi } from '../../services/api'
import { useLanguage } from '../../context/LanguageContext'

/**
 * Grouping edit / builder — Phase 5.
 *
 * Three-tab layout:
 *   Settings → name, center (map), radius slider, status, brand override
 *   Courses  → courses-in-radius live list with include/exclude/preferred per row
 *   Hotels   → curated hotel list (live Expedia search deferred to Phase 7)
 */
export default function GroupingEdit() {
  const { t } = useLanguage()
  const { id } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [grouping, setGrouping] = useState(null)
  const [courseList, setCourseList] = useState([])
  const [hotelList, setHotelList] = useState([])
  const [refundTiers, setRefundTiers] = useState([])
  const [teeTimeCourses, setTeeTimeCourses] = useState([])
  const [loading, setLoading] = useState(true)
  // Allow deep-linking to a tab via ?tab=packages (e.g. when returning from
  // the package editor). Falls back to 'settings' if not specified.
  const VALID_TABS = ['settings', 'courses', 'hotels', 'packages', 'bundles', 'branding', 'embed']
  const initialTab = VALID_TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'settings'
  const [tab, setTab] = useState(initialTab)
  const [error, setError] = useState(null)

  // Two refresh modes:
  //   - reload({ silent: false }) is used on first mount and shows a full
  //     "Loading…" shell while the request is in flight.
  //   - reload({ silent: true }) is used as onChanged from child tabs after
  //     a bulk action — the local hotel/course list state has already been
  //     updated optimistically, so we re-fetch in the background without
  //     blanking the UI. This kills the screen flash the reseller saw after
  //     clicking "Mark X as Excluded".
  function reload({ silent = false } = {}) {
    if (!silent) setLoading(true)
    setError(null)
    tournamentApi.get(`/portal/reseller/groupings/${id}`)
      .then(r => {
        setGrouping(r.data?.grouping || null)
        setCourseList(r.data?.courseList || [])
        setHotelList(r.data?.hotelList || [])
        setRefundTiers(r.data?.refundTiers || [])
        setTeeTimeCourses(r.data?.teeTimeCourses || [])
      })
      .catch(err => { if (!silent) setError(err?.response?.data?.message || err.message) })
      .finally(() => { if (!silent) setLoading(false) })
  }
  // First mount: full loading UI. Re-runs only when the grouping id in the
  // URL changes.
  useEffect(() => { reload({ silent: false }) }, [id])
  // Convenience handler for child tabs — they call this after their own
  // optimistic updates so we re-fetch in the background.
  const reloadSilent = () => reload({ silent: true })

  if (loading) return <ResellerShell title={t('sp_loading_grouping', 'Loading grouping…')}><div style={{ color: '#64748b' }}>{t('sp_loading_short', 'Loading…')}</div></ResellerShell>
  if (error || !grouping) {
    return (
      <ResellerShell title={t('sp_grouping', 'Grouping')}>
        <div style={errorBox}>{error || t('sp_grouping_not_found', 'Grouping not found.')}</div>
        <Link to="/portal/reseller/groupings" style={{ color: '#475569' }}>{t('sp_back_to_groupings', '← Back to groupings')}</Link>
      </ResellerShell>
    )
  }

  const isBundles = grouping?.GroupingType === 'bundles'
  return (
    <ResellerShell title={grouping.Name} activeNav={isBundles ? 'bundles' : 'groupings'}>
      <Link to={isBundles ? '/portal/reseller/golf-bundles' : '/portal/reseller/groupings'} style={{ color: '#475569', textDecoration: 'none', fontSize: 13 }}>← {isBundles ? t('sp_bundle_groups_all', 'All bundle groups') : t('sp_all_groupings_2', 'All groupings')}</Link>

      <div style={{ display: 'flex', gap: 4, background: '#fff', borderRadius: 8, padding: 4, border: '1px solid #e2e8f0', marginTop: 16, marginBottom: 16, width: 'fit-content' }}>
        <TabBtn active={tab === 'settings'} onClick={() => setTab('settings')}>{t('settings', 'Settings')}</TabBtn>
        <TabBtn active={tab === 'courses'}  onClick={() => setTab('courses')}>{t('page_courses', 'Courses')}</TabBtn>
        {!isBundles && <TabBtn active={tab === 'hotels'}   onClick={() => setTab('hotels')}>{t('sp_hotels', 'Hotels')}</TabBtn>}
        {!isBundles && <TabBtn active={tab === 'packages'} onClick={() => setTab('packages')}>{t('sp_nav_packages', 'Packages')}</TabBtn>}
        {isBundles  && <TabBtn active={tab === 'bundles'}  onClick={() => setTab('bundles')}>{t('sp_nav_golf_bundles', 'Golf Bundles')}</TabBtn>}
        <TabBtn active={tab === 'branding'} onClick={() => setTab('branding')}>{t('sp_nav_branding', 'Branding')}</TabBtn>
        <TabBtn active={tab === 'embed'}    onClick={() => setTab('embed')}>{t('page_embed', 'Embed')}</TabBtn>
      </div>

      {tab === 'settings' && <SettingsTab grouping={grouping} refundTiers={refundTiers} teeTimeCourses={teeTimeCourses} onSaved={reloadSilent} onArchived={() => navigate(isBundles ? '/portal/reseller/golf-bundles' : '/portal/reseller/groupings')} />}
      {tab === 'courses'  && <CoursesTab grouping={grouping} courseList={courseList} onChanged={reloadSilent} />}
      {tab === 'hotels'   && !isBundles && <HotelsTab grouping={grouping} hotelList={hotelList} onChanged={reloadSilent} />}
      {tab === 'packages' && !isBundles && <PackagesTab grouping={grouping} />}
      {tab === 'bundles'  && isBundles  && <BundlesTab grouping={grouping} />}
      {tab === 'branding' && <BrandingTab grouping={grouping} />}
      {tab === 'embed'    && <EmbedTab grouping={grouping} onChanged={reloadSilent} />}
    </ResellerShell>
  )
}

// ─── Settings tab ────────────────────────────────────────────────────────

function SettingsTab({ grouping, refundTiers = [], teeTimeCourses = [], onSaved, onArchived }) {
  const { t } = useLanguage()
  const [name, setName] = useState(grouping.Name)
  const [description, setDescription] = useState(grouping.Description || '')
  const [descriptionFr, setDescriptionFr] = useState(grouping.DescriptionFr || '')
  const [descLang, setDescLang] = useState('en') // which language the description editor shows
  const [lat, setLat] = useState(Number(grouping.CenterLatitude))
  const [lng, setLng] = useState(Number(grouping.CenterLongitude))
  const [centerLabel, setCenterLabel] = useState(grouping.CenterLabel || '')
  // Trip currency for this S&P region. Defaults to the destination country
  // (reverse-geocoded from the center pin); packages inherit it. Once the
  // reseller picks it manually, moving the pin no longer overrides it.
  const [centerCountryCode, setCenterCountryCode] = useState(grouping.CenterCountryCode || null)
  const [currency, setCurrency] = useState(grouping.Currency || 'CAD')
  const [currencyTouched, setCurrencyTouched] = useState(false)
  // Reverse-geocode the pin to get the country, and (unless the reseller set the
  // currency by hand) default the currency to it: US -> USD, else CAD.
  async function resolveCountryCurrency(a, b) {
    try {
      const { data } = await portalApi.get('/lookup/reverse-geocode', { params: { lat: a, lng: b } })
      const cc = data?.address?.countryCode || null
      if (cc) {
        setCenterCountryCode(cc)
        if (!currencyTouched) setCurrency(cc === 'US' ? 'USD' : 'CAD')
      }
    } catch { /* best effort — keep the current currency */ }
  }
  const [radiusKm, setRadiusKm] = useState(Number(grouping.RadiusKm))
  const [status, setStatus] = useState(grouping.Status)
  const [defaultLanguage, setDefaultLanguage] = useState(grouping.DefaultLanguage || 'en')
  const [allowTeeTimeOnly, setAllowTeeTimeOnly] = useState(!!grouping.AllowTeeTimeOnly)
  const [allowCustomTripRequests, setAllowCustomTripRequests] = useState(!!grouping.AllowCustomTripRequests)
  const [buildYourOwnEnabled, setBuildYourOwnEnabled] = useState(!!grouping.BuildYourOwnEnabled)
  const [buildYourOwnMarkupPercent, setBuildYourOwnMarkupPercent] = useState(
    grouping.BuildYourOwnMarkupPercent != null ? String(grouping.BuildYourOwnMarkupPercent) : '')
  const [customTripBannerStyle, setCustomTripBannerStyle] = useState(grouping.CustomTripBannerStyle || 'regular')
  const [teeTimeBannerStyle, setTeeTimeBannerStyle] = useState(grouping.TeeTimeBannerStyle || 'regular')
  // Optional default course for the embed tee-time picker. '' = none (list all
  // alphabetically). Stored as a course id string for the <select>.
  const [teeTimeDefaultCourseId, setTeeTimeDefaultCourseId] = useState(
    grouping.TeeTimeDefaultCourse_ID != null ? String(grouping.TeeTimeDefaultCourse_ID) : ''
  )
  // Tee-time cancellation/refund tiers (same shape + engine as package tiers).
  // Empty by default; the reseller adds day-window → refund% rows.
  const [tierRows, setTierRows] = useState(
    (refundTiers || []).map(t => ({
      daysBeforeMin: t.DaysBeforeMin,
      daysBeforeMax: t.DaysBeforeMax,
      refundPercent: Number(t.RefundPercent),
    }))
  )
  function addTier() { setTierRows(rs => [...rs, { daysBeforeMin: 0, daysBeforeMax: null, refundPercent: 0 }]) }
  function updateTier(i, key, val) { setTierRows(rs => rs.map((r, idx) => idx === i ? { ...r, [key]: val } : r)) }
  function removeTier(i) { setTierRows(rs => rs.filter((_, idx) => idx !== i)) }
  // Course-name highlight filter — passed to MapPicker so matching pins
  // render larger with an amber ring and the map zooms in.
  const [courseHighlight, setCourseHighlight] = useState('')
  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState(null)
  const [err, setErr] = useState(null)

  // All available courses across Canada — connected (green) and not
  // (red). Click handler opens a popup with Connect / Disconnect.
  // The endpoint geocodes TTC catalog rows on a budget per call, so
  // we re-fetch after a connect to pick up newly-geocoded entries.
  const [courseMarkers, setCourseMarkers] = useState([])
  const [popup, setPopup] = useState(null) // { course } | null
  const [busy, setBusy] = useState(false)
  function loadMarkers() {
    tournamentApi.get('/portal/reseller/all-courses-map')
      .then(r => {
        const list = (r.data?.courses || []).map(c => {
          // Provider shown after the course name in the map tooltip, matching the
          // partner API's apiProvider bucket: the tee-time.com aggregator = "Apex",
          // everything else (InteGolf tee sheet + TTC/TeeOn/etc. direct) = "InteGolf".
          const provider = c.externalProvider === 'teetime-com' ? 'Apex' : 'InteGolf'
          const named = `${c.name} (${provider})`
          return {
            lat: c.lat, lng: c.lng,
            // Only the tooltip label carries the provider; `name` stays clean so
            // the highlight search + click popup keep matching on the bare name.
            label: c.connected ? `✓ ${named}` : named,
            connected: c.connected,
            coursesId: c.coursesId,
            ttcCourseCode: c.ttcCourseCode,
            kind: c.kind,
            externalProvider: c.externalProvider || null,
            name: c.name,
            city: c.city,
            state: c.state,
          }
        })
        setCourseMarkers(list)
      })
      .catch(() => { /* non-fatal */ })
  }
  useEffect(loadMarkers, [])

  async function connectCourse(course) {
    setBusy(true)
    try {
      if (course.kind === 'ttc') {
        // Imports the TTC catalog row + creates the integration row.
        // Reseller still needs to set who-pays + per-round caps after,
        // but we get them onto the map immediately.
        await tournamentApi.post('/portal/reseller/courses/connect', {
          provider: 'ttc',
          ttcCourseCode: course.ttcCourseCode,
          courseName: course.name,
        })
      } else {
        // Local course — just attach to this reseller.
        await tournamentApi.post('/portal/reseller/courses', {
          coursesId: course.coursesId,
        })
      }
      setPopup(null)
      loadMarkers()
    } catch (e) {
      alert(e?.response?.data?.message || e.message)
    } finally { setBusy(false) }
  }

  async function disconnectCourse(course) {
    if (!confirm(t('sp_disconnect_confirm', 'Disconnect {name}? It will no longer appear in your packages.', { name: course.name }))) return
    setBusy(true)
    try {
      await tournamentApi.delete(`/portal/reseller/courses/${course.coursesId}`)
      setPopup(null)
      loadMarkers()
    } catch (e) {
      alert(e?.response?.data?.message || e.message)
    } finally { setBusy(false) }
  }

  async function save() {
    setSaving(true); setErr(null)
    try {
      // When the center or radius changed, warn about any curated courses
      // (preferred/included/excluded) that now fall OUTSIDE the new area —
      // moving or shrinking the circle drops them from this grouping. This
      // clears leftovers from a duplicated grouping re-pointed to a new region.
      let pruneOutOfRadiusRules = false
      const geoChanged = lat !== Number(grouping.CenterLatitude)
        || lng !== Number(grouping.CenterLongitude)
        || radiusKm !== Number(grouping.RadiusKm)
      if (geoChanged) {
        const { data } = await tournamentApi.get(
          `/portal/reseller/groupings/${grouping.ID_NO}/radius-impact`,
          { params: { lat, lng, radiusKm } }
        )
        const outside = data?.courses || []
        if (outside.length) {
          const list = outside
            .map(c => `• ${c.courseName}${c.city ? ' — ' + c.city : ''} (${c.listType})`)
            .join('\n')
          const ok = window.confirm(t('sp_radius_prune_confirm',
            '{n} curated course(s) now fall outside this area and will be removed from this grouping:\n\n{list}\n\nContinue?',
            { n: outside.length, list }))
          if (!ok) { setSaving(false); return }
          pruneOutOfRadiusRules = true
        }
      }
      await tournamentApi.put(`/portal/reseller/groupings/${grouping.ID_NO}`, {
        name, description, descriptionFr, centerLatitude: lat, centerLongitude: lng,
        centerLabel, centerCountryCode, currency, radiusKm, status, defaultLanguage,
        allowTeeTimeOnly: allowTeeTimeOnly ? 1 : 0,
        allowCustomTripRequests: allowCustomTripRequests ? 1 : 0,
        buildYourOwnEnabled: buildYourOwnEnabled ? 1 : 0,
        buildYourOwnMarkupPercent: buildYourOwnEnabled ? (buildYourOwnMarkupPercent === '' ? null : Number(buildYourOwnMarkupPercent)) : null,
        customTripBannerStyle,
        teeTimeBannerStyle,
        teeTimeDefaultCourseId: teeTimeDefaultCourseId === '' ? null : Number(teeTimeDefaultCourseId),
        pruneOutOfRadiusRules,
      })
      // Persist the tee-time refund tiers alongside the grouping settings.
      await tournamentApi.put(`/portal/reseller/groupings/${grouping.ID_NO}/refund-tiers`, {
        tiers: tierRows.map(r => ({
          daysBeforeMin: Number(r.daysBeforeMin) || 0,
          daysBeforeMax: r.daysBeforeMax === '' || r.daysBeforeMax == null ? null : Number(r.daysBeforeMax),
          refundPercent: Number(r.refundPercent) || 0,
        })),
      })
      setSavedAt(new Date())
      onSaved()
    } catch (e2) { setErr(e2?.response?.data?.message || e2.message) }
    finally { setSaving(false) }
  }

  async function archive() {
    if (!confirm(t('sp_archive_grouping_confirm', 'Archive this grouping? You can\'t undo this through the UI (database row preserved).'))) return
    try {
      await tournamentApi.delete(`/portal/reseller/groupings/${grouping.ID_NO}`)
      onArchived()
    } catch (e2) { alert(e2?.response?.data?.message || e2.message) }
  }

  return (
    <>
      {err && <div style={errorBox}>{err}</div>}
      {savedAt && <div style={{ ...successBox, marginBottom: 16 }}>{t('sp_saved_at_time', 'Saved at {time}.', { time: savedAt.toLocaleTimeString() })}</div>}

      <Section title={t('sp_basic_info', 'Basic info')}>
        <Field label={t('sp_name_label', 'Name')}><input style={inp} value={name} onChange={e => setName(e.target.value)} /></Field>
        <Field label={t('sp_grouping_description_label', 'Description (shown at the top of your embed)')}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            {[['en', t('sp_lang_english', 'English')], ['fr', t('sp_lang_french', 'Français')]].map(([code, label]) => (
              <button key={code} type="button" onClick={() => setDescLang(code)}
                style={{
                  padding: '4px 12px', fontSize: 12, fontWeight: 600, borderRadius: 6, cursor: 'pointer',
                  background: descLang === code ? '#0f172a' : '#fff',
                  color: descLang === code ? '#fff' : '#475569',
                  border: '1px solid ' + (descLang === code ? '#0f172a' : '#e2e8f0'),
                }}>{label}</button>
            ))}
          </div>
          {descLang === 'en'
            ? <textarea style={{ ...inp, minHeight: 60 }} value={description} onChange={e => setDescription(e.target.value)} placeholder={t('sp_grouping_desc_en_ph', 'English description…')} />
            : <textarea style={{ ...inp, minHeight: 60 }} value={descriptionFr} onChange={e => setDescriptionFr(e.target.value)} placeholder={t('sp_grouping_desc_fr_ph', 'Description en français…')} />}
        </Field>
        <div style={row}>
          <Field label={t('status', 'Status')} style={{ flex: 1 }}>
            <select value={status} onChange={e => setStatus(e.target.value)} style={inp}>
              <option value="draft">{t('draft', 'Draft')}</option>
              <option value="active">{t('active', 'Active')}</option>
              <option value="paused">{t('paused', 'Paused')}</option>
            </select>
          </Field>
          <Field label={t('sp_slug', 'Slug')} style={{ flex: 1 }}><input value={grouping.Slug} readOnly style={{ ...inp, background: '#f8fafc', color: '#94a3b8', fontFamily: 'monospace' }} /></Field>
        </div>
        <Field label={t('sp_grouping_default_language', 'Default customer language')}>
          <select value={defaultLanguage} onChange={e => setDefaultLanguage(e.target.value)} style={{ ...inp, maxWidth: 240 }}>
            <option value="en">{t('english', 'English')}</option>
            <option value="fr">{t('french', 'Français')}</option>
          </select>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{t('sp_grouping_default_language_help', 'The language this grouping’s embed opens in. Customers can switch it themselves on the embed.')}</div>
        </Field>
      </Section>

      <Section title={t('sp_booking_options', 'Booking options')}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
          <Toggle checked={allowTeeTimeOnly} onChange={setAllowTeeTimeOnly} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', cursor: 'pointer' }} onClick={() => setAllowTeeTimeOnly(v => !v)}>
              {t('sp_allow_tee_time_only', 'Allow individual tee-time booking')}
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
              {t('sp_allow_tee_time_only_help', "Adds a 'Book a tee time' option to this grouping's embed so customers can book a single round at one of your connected courses — no package required. Uses your normal pricing + payment. Remember to Save.")}
            </div>

            {/* Default course — optional. When set, the embed shows this course
                on its own and pre-selected, with the rest listed under an
                "Other courses" break. Options are exactly the bookable set the
                embed offers (package + included courses with a live golf feed). */}
            {allowTeeTimeOnly && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
                  {t('sp_tt_default_course', 'Default course')}
                </div>
                <select
                  value={teeTimeDefaultCourseId}
                  onChange={e => setTeeTimeDefaultCourseId(e.target.value)}
                  style={{ ...inp, maxWidth: 360 }}
                >
                  <option value="">{t('sp_tt_default_course_none', 'None (list all courses)')}</option>
                  {teeTimeCourses.map(c => (
                    <option key={c.coursesId} value={String(c.coursesId)}>
                      {c.name}{c.city ? ` — ${c.city}` : ''}
                    </option>
                  ))}
                </select>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6, maxWidth: 480 }}>
                  {t('sp_tt_default_course_help', 'Optional. If set, this course is shown on its own and pre-selected on the embed, with every other bookable course listed below under “Other courses”. Leave as “None” to list all courses alphabetically.')}
                </div>
              </div>
            )}

            {/* Prominence picker — only relevant when tee-time booking is on.
                Drives how much the "Book a tee time" banner stands out on the
                embed landing page. */}
            {allowTeeTimeOnly && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
                  {t('sp_tt_banner_style', 'Banner prominence')}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {[
                    { id: 'regular', label: t('sp_tt_style_regular', 'Regular'), help: t('sp_tt_style_regular_help', 'Subtle card') },
                    { id: 'bold',    label: t('sp_tt_style_bold', 'Bold'),       help: t('sp_tt_style_bold_help', 'Brand outline, larger') },
                    { id: 'extreme', label: t('sp_tt_style_extreme', 'Extreme'), help: t('sp_tt_style_extreme_help', 'Full brand-colour banner') },
                  ].map(opt => {
                    const active = teeTimeBannerStyle === opt.id
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setTeeTimeBannerStyle(opt.id)}
                        style={{
                          textAlign: 'left', padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
                          background: active ? '#0f172a' : '#fff',
                          color: active ? '#fff' : '#0f172a',
                          border: '1px solid ' + (active ? '#0f172a' : '#cbd5e1'),
                          minWidth: 130,
                        }}
                      >
                        <div style={{ fontSize: 13, fontWeight: 700 }}>{opt.label}</div>
                        <div style={{ fontSize: 11, opacity: active ? 0.85 : 0.6, marginTop: 1 }}>{opt.help}</div>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Tee-time cancellation / refund policy — same tier engine as
                packages. Each row: from/to days before the tee time → refund %.
                Without any tier the customer can't self-cancel (falls back to
                "contact us"). */}
            {allowTeeTimeOnly && (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 2 }}>
                  {t('sp_tt_refund_policy', 'Cancellation / refund policy')}
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>
                  {t('sp_tt_refund_policy_help', 'Days before the tee time → refund %. Leave “To” blank for “or more”. No tiers = customers must contact you to cancel.')}
                </div>
                {tierRows.length > 0 && (
                  <table style={{ borderCollapse: 'collapse', marginBottom: 8 }}>
                    <thead>
                      <tr>
                        <th style={ttTh}>{t('sp_tt_refund_from', 'From (days before)')}</th>
                        <th style={ttTh}>{t('sp_tt_refund_to', 'To (days before)')}</th>
                        <th style={ttTh}>{t('sp_tt_refund_pct', 'Refund %')}</th>
                        <th style={ttTh}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {tierRows.map((r, i) => (
                        <tr key={i}>
                          <td style={ttTd}><input type="number" min={0} style={{ ...inp, width: 110 }} value={r.daysBeforeMin} onChange={e => updateTier(i, 'daysBeforeMin', e.target.value)} /></td>
                          <td style={ttTd}><input type="number" min={0} placeholder="—" style={{ ...inp, width: 110 }} value={r.daysBeforeMax ?? ''} onChange={e => updateTier(i, 'daysBeforeMax', e.target.value === '' ? null : e.target.value)} /></td>
                          <td style={ttTd}><input type="number" min={0} max={100} step="0.01" style={{ ...inp, width: 90 }} value={r.refundPercent} onChange={e => updateTier(i, 'refundPercent', e.target.value)} /></td>
                          <td style={ttTd}><button type="button" onClick={() => removeTier(i)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 13 }}>{t('sp_remove', 'Remove')}</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                <button type="button" onClick={addTier} style={{ padding: '6px 12px', background: '#fff', color: '#0f172a', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                  + {t('sp_tt_add_refund_tier', 'Add refund tier')}
                </button>
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: 12, marginTop: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
          <Toggle checked={allowCustomTripRequests} onChange={setAllowCustomTripRequests} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', cursor: 'pointer' }} onClick={() => setAllowCustomTripRequests(v => !v)}>
              {t('sp_allow_custom_trip_requests', 'Allow custom trip requests')}
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
              {t('sp_allow_custom_trip_requests_help', "Adds a 'Plan my trip' concierge form to this grouping's embed so customers can request a hand-built trip (dates, golfers, rooms, preferred courses, notes). Nothing is charged — it arrives as a request in Custom Trips for you to price and send. Remember to Save.")}
            </div>

            {/* Prominence picker — how much the "Plan my trip" banner stands out
                on the embed (shown below the packages). Mirrors the tee-time one. */}
            {allowCustomTripRequests && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
                  {t('sp_tt_banner_style', 'Banner prominence')}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {[
                    { id: 'regular', label: t('sp_tt_style_regular', 'Regular'), help: t('sp_tt_style_regular_help', 'Subtle card') },
                    { id: 'bold',    label: t('sp_tt_style_bold', 'Bold'),       help: t('sp_tt_style_bold_help', 'Brand outline, larger') },
                    { id: 'extreme', label: t('sp_tt_style_extreme', 'Extreme'), help: t('sp_tt_style_extreme_help', 'Full brand-colour banner') },
                  ].map(opt => {
                    const active = customTripBannerStyle === opt.id
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setCustomTripBannerStyle(opt.id)}
                        style={{
                          textAlign: 'left', padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
                          background: active ? '#0f172a' : '#fff',
                          color: active ? '#fff' : '#0f172a',
                          border: '1px solid ' + (active ? '#0f172a' : '#cbd5e1'),
                          minWidth: 130,
                        }}
                      >
                        <div style={{ fontSize: 13, fontWeight: 700 }}>{opt.label}</div>
                        <div style={{ fontSize: 11, opacity: active ? 0.85 : 0.6, marginTop: 1 }}>{opt.help}</div>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: 12, marginTop: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
          <Toggle checked={buildYourOwnEnabled} onChange={setBuildYourOwnEnabled} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', cursor: 'pointer' }} onClick={() => setBuildYourOwnEnabled(v => !v)}>
              {t('sp_allow_byo', 'Allow build-your-own trips')}
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
              {t('sp_allow_byo_help', "Adds a 'Build your own trip' card to this embed. Customers pick their own hotel and rounds and are priced from cost + your markup below (no fixed package). Great for price-sensitive shoppers. Remember to Save.")}
            </div>
            {buildYourOwnEnabled && (
              <div style={{ marginTop: 12, maxWidth: 260 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
                  {t('sp_byo_markup', 'Your markup on cost (%)')}
                </div>
                <input type="number" min={0} max={100} step="0.5" value={buildYourOwnMarkupPercent}
                  onChange={e => setBuildYourOwnMarkupPercent(e.target.value)} placeholder="10"
                  style={{ ...inp, width: 120 }} />
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                  {t('sp_byo_markup_help', 'Added on top of the hotel and golf costs.')}
                </div>
              </div>
            )}
          </div>
        </div>
      </Section>

      <Section title={t('sp_center_radius', 'Center + radius')} subtitle={t('sp_center_radius_subtitle', "Pin the destination center and size the radius. Green dots are courses you've connected; red dots are available but not connected — click any to connect or disconnect.")}>
        {/* 2-column layout: text controls on the left, map on the right.
            Map is sized roughly square (520px tall) so the user can see
            the surrounding course pins instead of a wide-short strip.
            Collapses to single-column on narrow screens. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 360px) 1fr', gap: 16, alignItems: 'start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Field
              label={t('sp_highlight_course_on_map', 'Highlight a course on the map')}
              hint={t('sp_highlight_course_hint', 'Type any part of a course name to find it on the map. Matching pins grow larger with a yellow ring and the map zooms to fit them. Clear the field to reset.')}
            >
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={courseHighlight}
                  onChange={e => setCourseHighlight(e.target.value)}
                  placeholder={t('sp_highlight_course_placeholder', 'e.g. Lac Etchemin')}
                  style={{ ...inp, paddingRight: courseHighlight ? 28 : undefined }}
                />
                {courseHighlight && (
                  <button
                    type="button"
                    onClick={() => setCourseHighlight('')}
                    title={t('sp_clear', 'Clear')}
                    style={{
                      position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                      width: 20, height: 20, borderRadius: 10, border: 'none',
                      background: '#e2e8f0', color: '#475569',
                      cursor: 'pointer', fontSize: 13, lineHeight: 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >×</button>
                )}
              </div>
            </Field>
            <Field label={t('sp_radius_km', 'Radius: {km} km', { km: radiusKm })}>
              <input type="range" min="1" max="500" step="1" value={radiusKm} onChange={e => setRadiusKm(Number(e.target.value))} style={{ width: '100%' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                <span>{t('sp_grouping_radius_min', '1 km')}</span><span>{t('sp_grouping_radius_max', '500 km')}</span>
              </div>
            </Field>
            <div style={row}>
              <Field label={t('latitude', 'Latitude')} style={{ flex: 1 }}><input type="number" step="0.0001" value={lat} onChange={e => setLat(Number(e.target.value))} style={{ ...inp, fontFamily: 'monospace' }} /></Field>
              <Field label={t('longitude', 'Longitude')} style={{ flex: 1 }}><input type="number" step="0.0001" value={lng} onChange={e => setLng(Number(e.target.value))} style={{ ...inp, fontFamily: 'monospace' }} /></Field>
            </div>
            <Field label={t('sp_center_label_display', 'Center label (display)')}><input style={inp} value={centerLabel} onChange={e => setCenterLabel(e.target.value)} placeholder={t('sp_center_label_placeholder', 'e.g. Sydney, NS, Canada')} /></Field>
            <Field label={t('sp_trip_currency', 'Trip currency')} hint={t('sp_trip_currency_hint', 'Defaults to the pinned destination (US = USD, else CAD). Packages in this region inherit it.')}>
              <select style={inp} value={currency} onChange={e => { setCurrency(e.target.value); setCurrencyTouched(true) }}>
                <option value="CAD">CAD</option>
                <option value="USD">USD</option>
              </select>
            </Field>
          </div>
          <MapPicker
            lat={lat} lng={lng}
            radiusKm={radiusKm}
            markers={courseMarkers}
            onMarkerClick={(c) => {
              // MapPicker emits either a single course or a cluster:
              // { cluster: true, items: [...] }. Normalize so the popup
              // always works against an array; single-course popups use
              // an array of length 1.
              if (c && c.cluster) setPopup({ courses: c.items })
              else setPopup({ courses: [c] })
            }}
            onChange={({ lat: a, lng: b, address }) => { setLat(a); setLng(b); if (address) setCenterLabel(address); resolveCountryCurrency(a, b) }}
            height={520}
            highlightFilter={courseHighlight}
          />
        </div>
        {popup && popup.courses && popup.courses.length > 0 && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
               onClick={() => !busy && setPopup(null)}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, padding: 24, width: 460, maxWidth: '90vw', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 50px rgba(0,0,0,0.2)' }}>
              {popup.courses.length > 1 && (
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
                  {t('sp_courses_at_location', '{count} courses at this location', { count: popup.courses.length })}
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {popup.courses.map((c, i) => (
                  <div key={c.coursesId || c.ttcCourseCode || i}
                       style={{
                         padding: 14,
                         background: c.connected ? '#f0fdf4' : '#fef2f2',
                         border: '1px solid ' + (c.connected ? '#86efac' : '#fecaca'),
                         borderRadius: 8,
                       }}>
                    <div style={{ fontSize: 11, color: c.connected ? '#16a34a' : '#dc2626', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>
                      {c.connected ? t('sp_connected', 'Connected') : t('sp_not_connected', 'Not connected')}
                    </div>
                    <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>{c.name}</h3>
                    <div style={{ color: '#64748b', fontSize: 12, marginBottom: 10 }}>
                      {[c.city, c.state].filter(Boolean).join(', ')}
                      {c.kind === 'ttc' && <span style={{ marginLeft: 6, padding: '1px 6px', background: '#dbeafe', color: '#1e40af', borderRadius: 4, fontSize: 11 }}>TTC</span>}
                      {c.kind === 'external' && <span style={{ marginLeft: 6, padding: '1px 6px', background: '#f3e8ff', color: '#7e22ce', borderRadius: 4, fontSize: 11 }}>{t('sp_via_provider', 'via {provider}', { provider: c.externalProvider === 'teetime-com' ? 'tee-time.com (Apex)' : c.externalProvider })}</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      {c.connected
                        ? <button type="button" onClick={() => disconnectCourse(c)} disabled={busy} style={{ ...primaryBtn, background: '#dc2626', padding: '6px 12px', fontSize: 12 }}>{busy ? '…' : t('sp_disconnect', 'Disconnect')}</button>
                        : <button type="button" onClick={() => connectCourse(c)} disabled={busy} style={{ ...primaryBtn, background: '#16a34a', padding: '6px 12px', fontSize: 12 }}>{busy ? '…' : t('sp_connect', 'Connect')}</button>}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
                <button type="button" onClick={() => setPopup(null)} disabled={busy} style={secondaryBtn}>{t('sp_close', 'Close')}</button>
              </div>
            </div>
          </div>
        )}
      </Section>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 0', borderTop: '1px solid #e2e8f0' }}>
        <button onClick={archive} style={{ ...secondaryBtn, color: '#dc2626', borderColor: '#fecaca' }}>{t('sp_archive_grouping', 'Archive grouping')}</button>
        <button onClick={save} disabled={saving} style={{ ...primaryBtn, padding: '12px 24px', opacity: saving ? 0.6 : 1 }}>
          {saving ? t('sp_saving', 'Saving…') : t('sp_save_changes', 'Save changes')}
        </button>
      </div>
    </>
  )
}

// ─── Courses tab ────────────────────────────────────────────────────────

function CoursesTab({ grouping, courseList, onChanged }) {
  const { t } = useLanguage()
  const [inRadius, setInRadius] = useState([])
  // Meta from the API: total available before the cap, how many are visible,
  // and whether the cap actually truncated. Drives the inline cap editor +
  // the "showing X of Y" message.
  const [meta, setMeta] = useState({ total: 0, visible: 0, truncated: false, maxCoursesInRadius: null })
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  // Inline editor state for the cap. Lives in the cap-editor block at the
  // top of the tab so the reseller can adjust the limit right where they
  // see the consequence, without bouncing to Settings.
  const [capInput, setCapInput] = useState(
    grouping.MaxCoursesInRadius == null ? '' : String(grouping.MaxCoursesInRadius)
  )
  const [capSaving, setCapSaving] = useState(false)
  const [capJustSaved, setCapJustSaved] = useState(false)

  function reload() {
    setLoading(true); setErr(null)
    tournamentApi.get(`/portal/reseller/groupings/${grouping.ID_NO}/courses-in-radius`)
      .then(r => {
        setInRadius(r.data?.courses || [])
        setMeta({
          total:               r.data?.total ?? (r.data?.courses?.length || 0),
          visible:             r.data?.visible ?? (r.data?.courses?.length || 0),
          truncated:           !!r.data?.truncated,
          maxCoursesInRadius:  r.data?.maxCoursesInRadius ?? null,
        })
      })
      .catch(e => setErr(e?.response?.data?.message || e.message))
      .finally(() => setLoading(false))
  }
  useEffect(reload, [grouping.ID_NO])

  async function saveCap() {
    const trimmed = String(capInput || '').trim()
    const value = trimmed === '' ? null : Number(trimmed)
    // Validate locally so we don't bother the server with bad numbers.
    if (value !== null && (!Number.isFinite(value) || value < 1 || value > 500)) {
      alert(t('sp_cap_range_invalid', 'Pick a number between 1 and 500, or leave blank for no limit.'))
      return
    }
    setCapSaving(true); setCapJustSaved(false)
    try {
      await tournamentApi.put(`/portal/reseller/groupings/${grouping.ID_NO}`, {
        maxCoursesInRadius: value,
      })
      setCapJustSaved(true)
      setTimeout(() => setCapJustSaved(false), 2500)
      onChanged() // refresh the parent grouping so it picks up the new cap
      reload()    // refresh the list (more or fewer courses now)
    } catch (e) {
      alert(e?.response?.data?.message || e.message || t('sp_could_not_save_limit', 'Could not save the limit.'))
    } finally { setCapSaving(false) }
  }

  async function setListType(coursesId, listType) {
    try {
      if (listType === 'none') {
        await tournamentApi.delete(`/portal/reseller/groupings/${grouping.ID_NO}/course-list/${coursesId}`)
      } else {
        await tournamentApi.post(`/portal/reseller/groupings/${grouping.ID_NO}/course-list`, { coursesId, listType })
      }
      reload(); onChanged()
    } catch (e) { alert(e?.response?.data?.message || e.message) }
  }

  return (
    <>
      <p style={{ color: '#64748b', marginTop: 0, marginBottom: 16, fontSize: 14, maxWidth: 720 }}>
        {t('sp_courses_in_radius_intro', 'Courses inside your {radius}km radius around {center}. Mark each as preferred (bumps to top), excluded (hidden from the customer), or included (added even if outside radius).', { radius: grouping.RadiusKm, center: grouping.CenterLabel || t('sp_the_center_pin', 'the center pin') })}
      </p>

      {/* Inline cap editor — the reseller adjusts this right where they
          see the consequence, instead of bouncing to Settings.
          Always renders so the explanation is visible even when the
          cap isn't actively trimming the list. */}
      <div style={{
        marginBottom: 16, padding: 14,
        background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8,
        maxWidth: 760,
      }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#1e40af', marginBottom: 4 }}>
          {t('sp_max_courses_shown', 'Max courses shown to customers')}
        </div>
        <div style={{ fontSize: 12, color: '#1e40af', marginBottom: 10, lineHeight: 1.5 }}>
          {t('sp_grouping_cap_lead', 'We cap how many courses inside the radius appear in your embed code (and on this tab). ')}
          <strong>{t('sp_grouping_cap_default_15', 'Default is 15')}</strong>{t('sp_grouping_cap_closest_first', ' — the closest courses to your center pin show first.')}{' '}
          <strong>{t('sp_grouping_cap_want_more', 'Want your customers to see more options? Raise the number below.')}</strong>{' '}
          {t('sp_grouping_cap_leave_blank', 'Leave blank to show every course in the radius.')}
          {meta.truncated && (
            <>
              {' '}<br />
              <span style={{ fontWeight: 600 }}>
                {t('sp_showing_closest_x_of_y', "Right now you're showing the closest {visible} of {total} courses inside the radius.", { visible: meta.visible, total: meta.total })}
              </span>
            </>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="number"
            min="1"
            max="500"
            value={capInput}
            onChange={e => setCapInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') saveCap() }}
            placeholder={t('sp_grouping_cap_placeholder', '15 (default)')}
            style={{ ...inp, maxWidth: 140 }}
            disabled={capSaving}
          />
          <button
            type="button"
            onClick={saveCap}
            disabled={capSaving}
            style={{ ...primaryBtn, padding: '8px 16px', fontSize: 13 }}
          >
            {capSaving ? t('sp_saving', 'Saving…') : t('sp_update_limit', 'Update limit')}
          </button>
          {capJustSaved && (
            <span style={{ fontSize: 12, color: '#15803d', fontWeight: 600 }}>{t('sp_saved_check', '✓ Saved')}</span>
          )}
          <span style={{ fontSize: 11, color: '#1e40af' }}>
            {t('sp_current_label', 'Current:')} <strong>{meta.maxCoursesInRadius == null ? t('sp_no_limit', 'No limit') : meta.maxCoursesInRadius}</strong>
            {' · '}{meta.total} {meta.total === 1 ? t('sp_grouping_course_lower', 'course') : t('sp_courses_lower', 'courses')} {t('sp_in_radius', 'in radius')}
          </span>
        </div>
      </div>

      {err && <div style={errorBox}>{err}</div>}
      {loading
        ? <div style={empty}>{t('sp_loading_courses', 'Loading courses…')}</div>
        : inRadius.length === 0
          ? <div style={empty}>{t('sp_no_courses_in_radius', 'No courses inside this radius. Try expanding it on the Settings tab, or add a course manually via "include" once we surface the search-add UI here in a follow-up pass.')}</div>
          : (
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
              <table style={tbl}>
                <thead>
                  <tr style={tr}>
                    <th style={th}>{t('sp_grouping_th_course', 'Course')}</th>
                    <th style={th}>{t('distance', 'Distance')}</th>
                    <th style={th}>{t('sp_col_integration', 'Integration')}</th>
                    <th style={th}>{t('sp_col_list_type', 'List type')}</th>
                  </tr>
                </thead>
                <tbody>
                  {inRadius.map(c => (
                    <tr key={c.Courses_ID} style={tr}>
                      <td style={td}><strong>{c.CourseName}</strong><div style={{ fontSize: 12, color: '#64748b' }}>{c.City}</div></td>
                      <td style={tdMono}>{c.DistanceKm != null ? `${c.DistanceKm} km` : '—'}</td>
                      <td style={td}>
                        {c.HasIntegration
                          ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <span style={{ color: '#16a34a', fontSize: 12, fontWeight: 600 }}>{t('sp_bookable', '● Bookable')}</span>
                                {c.ProviderClasses && (
                                  <span style={{ fontSize: 11, color: '#475569' }}>{c.ProviderClasses}</span>
                                )}
                              </div>
                            )
                          : <span style={{ color: '#92400e', fontSize: 12 }}>{t('sp_manual', '○ Manual')}</span>}
                      </td>
                      <td style={td}>
                        <select value={c.ListType || 'none'} onChange={e => setListType(c.Courses_ID, e.target.value)} style={selectStyle}>
                          <option value="none">{t('sp_list_default_auto', '— Default (auto)')}</option>
                          <option value="preferred">{t('sp_list_preferred_bump', 'Preferred (bump to top)')}</option>
                          <option value="exclude">{t('sp_list_exclude_hide', 'Exclude (hide from customers)')}</option>
                          <option value="include">{t('sp_list_include_force', 'Include (force-add)')}</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
      }

      {courseList.filter(c => c.ListType === 'exclude').length > 0 && (
        <div style={{ marginTop: 24, padding: 16, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8 }}>
          <strong style={{ fontSize: 13, color: '#991b1b' }}>{t('sp_excluded_courses', 'Excluded courses')}</strong>
          <ul style={{ margin: '6px 0 0 20px', fontSize: 13, color: '#7f1d1d' }}>
            {courseList.filter(c => c.ListType === 'exclude').map(c => <li key={c.Courses_ID}>{c.CourseName} — {c.City}</li>)}
          </ul>
        </div>
      )}
    </>
  )
}

// ─── Hotels tab ─────────────────────────────────────────────────────────

// Haversine distance in kilometres between two lat/lng points. Inlined
// here so the Hotels tab can rank by distance without pulling in a
// utility module — used only by the sort dropdown's "Nearest to center"
// option. ~0.5% error vs. true great-circle distance, plenty for ranking.
function haversineKmShort(lat1, lng1, lat2, lng2) {
  const R = 6371
  const toRad = (d) => d * Math.PI / 180
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)
  const a = Math.sin(dLat / 2) ** 2
          + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)))
}

function HotelsTab({ grouping, hotelList, onChanged }) {
  const { t } = useLanguage()
  const [hotels, setHotels] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [minStars, setMinStars] = useState(0)
  const [savingId, setSavingId] = useState(null)
  const [cached, setCached] = useState(false)
  // Tier 1+2 filter rules — saved on the grouping row.
  const [rules, setRules] = useState(grouping.HotelFilters
    ? (typeof grouping.HotelFilters === 'string' ? JSON.parse(grouping.HotelFilters) : grouping.HotelFilters)
    : {})
  const [savingRules, setSavingRules] = useState(false)
  // Opt-in "show estimated nightly rates" toggle. When on, the backend
  // does an extra Expedia rate-availability call for a sample stay and
  // attaches `cheapestNightly` to each hotel (markup baked in). Off by
  // default since the price call costs an API hit and many resellers
  // don't need rates while curating include/exclude lists.
  const [withRates, setWithRates] = useState(false)
  const [rateLookup, setRateLookup] = useState(null)
  // Min/max nightly price filters — only meaningful when withRates is on.
  // Empty string = no bound on that side. Hotels without a fetched rate
  // (cheapestNightly == null) drop out as soon as either bound is set,
  // since we can't compare them.
  const [minPrice, setMinPrice] = useState('')
  const [maxPrice, setMaxPrice] = useState('')
  // Pagination — render a slice of `visible`, not the whole 500-row grid.
  // Bulk-action verbs still target the full visible set so the reseller
  // can "Exclude all 556" with one click without flipping through pages.
  const [pageSize, setPageSize] = useState(25)
  const [page, setPage] = useState(1)
  // Sort mode applied after rule + search filtering but before pagination.
  //   rating   — star DESC, then guest rating DESC (matches backend default)
  //   alpha    — name A → Z
  //   distance — nearest to the grouping's center pin first
  //   price    — cheapest first (only meaningful when "Show estimated nightly rates" is on)
  const [sortMode, setSortMode] = useState('rating')
  // Curation-status filter: show only hotels the reseller marked Preferred /
  // Included / Excluded. 'all' = the normal in-radius preview. When a specific
  // status is picked we filter the FULL list by listType so excluded hotels
  // (which the preview hides) are findable.
  const [ruleStatusFilter, setRuleStatusFilter] = useState('all')

  async function load() {
    setLoading(true); setError(null)
    try {
      const params = new URLSearchParams({ minStars: String(minStars) })
      if (withRates) params.set('withRates', '1')
      const { data } = await tournamentApi.get(
        `/portal/reseller/groupings/${grouping.ID_NO}/hotels-in-radius?${params.toString()}`
      )
      setHotels(data.hotels || [])
      setCached(!!data.cached)
      setRateLookup(data.rateLookup || null)
    } catch (e) {
      setError(e?.response?.data?.message || e.message)
    } finally { setLoading(false) }
  }
  // useEffect can't be async (it'd return a Promise that React mistakes
  // for a cleanup function). Call the async loader from inside a sync wrapper.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() }, [minStars, grouping.ID_NO, withRates])
  // Snap back to page 1 whenever the filtered set changes shape — keeps
  // the reseller from staring at "page 8" when their search reduced the
  // set to 12 hotels. Sort changes too: a re-order reshuffles which row
  // is on which page, so dropping back to page 1 is the least surprising.
  useEffect(() => { setPage(1) }, [search, minStars, pageSize, rules, sortMode, minPrice, maxPrice, ruleStatusFilter])
  // If the reseller turns rates OFF, the price-based sort modes no longer
  // make sense (there's no price to sort by) so revert to the default.
  // Likewise clear the min/max bounds so they don't silently hide hotels
  // the next time rates come back on.
  useEffect(() => {
    if (!withRates) {
      if (sortMode === 'price-asc' || sortMode === 'price-desc' || sortMode === 'price') setSortMode('rating')
      setMinPrice(''); setMaxPrice('')
    }
  }, [withRates]) // eslint-disable-line react-hooks/exhaustive-deps

  // Bulk-apply a listType to every hotel that currently passes the filter
  // rules + name/city search. UI calls this from the "Apply to filtered"
  // buttons in the filter card. Backend exposes a single bulk endpoint
  // so we don't fan out N HTTP calls from the browser.
  const [bulkBusy, setBulkBusy] = useState(false)
  async function bulkApply(listType) {
    if (!filteredVisible.length) return
    const verb = listType === 'include' ? t('sp_verb_reset_include', 'reset to default Include') : t('sp_verb_mark_as', 'mark as {listType}', { listType })
    if (!window.confirm(t('sp_bulk_confirm', 'This will {verb} for {count} hotels that match the current filters. Continue?', { verb, count: filteredVisible.length }))) return
    setBulkBusy(true)
    try {
      // Hotels can now come from multiple providers (Expedia, RateHawk, …). The
      // bulk endpoint takes one source per call, so group by provider and fan
      // out one call per distinct source.
      const bySource = new Map()
      for (const h of filteredVisible) {
        const src = h.source || 'expedia'
        if (!bySource.has(src)) bySource.set(src, [])
        bySource.get(src).push({ hotelExternalId: h.externalId, hotelNamePreview: h.name, cityPreview: h.city || '' })
      }
      for (const [src, targets] of bySource) {
        await tournamentApi.post(`/portal/reseller/groupings/${grouping.ID_NO}/hotel-list/bulk`, {
          source: src,
          listType, // 'preferred' | 'include' | 'exclude' (or 'reset' to clear row)
          hotels: targets,
        })
      }
      // Optimistic local update for the bulk set (match by provider + id).
      const keys = new Set(filteredVisible.map(h => `${h.source || 'expedia'}:${h.externalId}`))
      setHotels(prev => prev.map(x => keys.has(`${x.source || 'expedia'}:${x.externalId}`)
        ? { ...x, listType: listType === 'reset' ? null : listType }
        : x
      ))
      onChanged?.()
    } catch (e) {
      alert(e?.response?.data?.message || e.message)
    } finally { setBulkBusy(false) }
  }

  async function setListType(h, listType) {
    const src = h.source || 'expedia'
    setSavingId(`${src}:${h.externalId}`)
    try {
      if (listType === null) {
        await tournamentApi.delete(`/portal/reseller/groupings/${grouping.ID_NO}/hotel-list/${encodeURIComponent(h.externalId)}?source=${encodeURIComponent(src)}`)
      } else {
        await tournamentApi.post(`/portal/reseller/groupings/${grouping.ID_NO}/hotel-list`, {
          hotelExternalId: h.externalId,
          source:          src,
          hotelNamePreview: h.name,
          cityPreview:     h.city || '',
          listType,
        })
      }
      // Optimistic local update (match by provider + id); reload curated counts on parent.
      setHotels(prev => prev.map(x => (x.externalId === h.externalId && (x.source || 'expedia') === src) ? { ...x, listType } : x))
      onChanged?.()
    } catch (e) {
      alert(e?.response?.data?.message || e.message)
    } finally { setSavingId(null) }
  }

  // Save filter rules to the grouping row. We do NOT call onChanged()
  // here — the parent's reload() flips loading=true which would
  // unmount this tab and reset the open/closed state of the filter
  // panel. Local state already reflects the new rules optimistically.
  async function saveRules(next) {
    setSavingRules(true)
    setRules(next)
    try {
      await tournamentApi.put(`/portal/reseller/groupings/${grouping.ID_NO}`, { hotelFilters: next })
    } catch (e) {
      alert(e?.response?.data?.message || e.message)
    } finally { setSavingRules(false) }
  }

  // Apply rules to the live hotel list so the reseller can see what
  // their customers will see. Mirrors the backend filterRules logic.
  const ruleFiltered = applyRulesPreview(hotels, rules)
  // Effective price bounds. Only applied when withRates is on (otherwise
  // every hotel has cheapestNightly = null, which would mass-hide them).
  const minPriceN = withRates && minPrice !== '' && Number.isFinite(Number(minPrice)) ? Number(minPrice) : null
  const maxPriceN = withRates && maxPrice !== '' && Number.isFinite(Number(maxPrice)) ? Number(maxPrice) : null
  const priceFilterActive = minPriceN != null || maxPriceN != null
  // When a curation status is selected, base the list on the full hotel set
  // filtered by listType (so Excluded hotels appear — the radius preview drops
  // them); otherwise use the normal in-radius preview.
  const statusBase = ruleStatusFilter === 'all'
    ? ruleFiltered
    : hotels.filter(h => (h.listType || null) === ruleStatusFilter)
  const filteredVisible = statusBase.filter(h => {
    if (search) {
      const s = search.toLowerCase()
      if (!((h.name || '').toLowerCase().includes(s) || (h.city || '').toLowerCase().includes(s))) return false
    }
    if (priceFilterActive) {
      const v = h.cheapestNightly == null ? null : Number(h.cheapestNightly)
      // Drop hotels with no known rate when a price bound is set — the
      // reseller is filtering on price, so showing "unknown" rows would
      // be misleading.
      if (v == null) return false
      if (minPriceN != null && v < minPriceN) return false
      if (maxPriceN != null && v > maxPriceN) return false
    }
    return true
  })
  const droppedByRules = hotels.length - ruleFiltered.length

  // Sort the filtered set per the dropdown choice. Sorted BEFORE pagination
  // so the "Showing 1-25 of 556" slice reflects the picked order. The
  // backend already returns hotels in rating order so 'rating' is a no-op
  // copy — we still wrap in a sort to keep the data immutable across
  // re-renders and so the comparator stays in one place.
  const centerLat = Number(grouping.CenterLatitude)
  const centerLng = Number(grouping.CenterLongitude)
  function hotelDistanceKm(h) {
    if (!h.location || !Number.isFinite(centerLat) || !Number.isFinite(centerLng)) return Infinity
    return haversineKmShort(centerLat, centerLng, Number(h.location.lat), Number(h.location.lng))
  }
  const sortedVisible = (() => {
    const list = [...filteredVisible]
    if (sortMode === 'alpha') {
      list.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }))
    } else if (sortMode === 'distance') {
      list.sort((a, b) => hotelDistanceKm(a) - hotelDistanceKm(b))
    } else if (sortMode === 'price' || sortMode === 'price-asc') {
      // Cheapest first; hotels with no rate available drop to the end.
      list.sort((a, b) => {
        const av = a.cheapestNightly == null ? Infinity : Number(a.cheapestNightly)
        const bv = b.cheapestNightly == null ? Infinity : Number(b.cheapestNightly)
        return av - bv
      })
    } else if (sortMode === 'price-desc') {
      // Priciest first; hotels with no rate available drop to the end
      // (treated as -Infinity for the comparison so they sink to the
      // bottom under DESC the same way they sink under ASC).
      list.sort((a, b) => {
        const av = a.cheapestNightly == null ? -Infinity : Number(a.cheapestNightly)
        const bv = b.cheapestNightly == null ? -Infinity : Number(b.cheapestNightly)
        return bv - av
      })
    } else {
      // 'rating' (default) — star DESC, then guest DESC.
      list.sort((a, b) =>
        (b.starRating || 0) - (a.starRating || 0)
        || (b.guestRating || 0) - (a.guestRating || 0)
      )
    }
    return list
  })()

  // `visible` is the FULL filtered+sorted set (used by bulk actions + the
  // "X locations" count). `paged` is the slice that actually renders.
  // Bulk-action verbs work off `visible` so a single click still applies
  // to all 556 even when only 25 are on-screen.
  const visible = sortedVisible
  const totalFiltered = visible.length
  const totalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(totalFiltered / pageSize))
  const safePage = Math.min(page, totalPages)
  const paged = pageSize === 'all' ? visible : visible.slice((safePage - 1) * pageSize, safePage * pageSize)
  const pageStartIdx = pageSize === 'all' || totalFiltered === 0 ? 0 : (safePage - 1) * pageSize + 1
  const pageEndIdx   = pageSize === 'all' ? totalFiltered : Math.min(safePage * pageSize, totalFiltered)

  return (
    <>
      <p style={{ color: '#64748b', marginTop: 0, marginBottom: 16, fontSize: 14, maxWidth: 720 }}>
        {t('sp_hotels_intro', "Live hotel search inside this grouping's {radius} km radius circle. Set filter rules below to narrow what customers see. Pin individual hotels to the top with an optional badge, or hide them entirely.", { radius: grouping.RadiusKm })}
      </p>

      <FilterRulesCard
        rules={rules}
        onChange={saveRules}
        saving={savingRules}
        matchCount={ruleFiltered.length}
        onBulkApply={bulkApply}
        bulkBusy={bulkBusy}
      />

      {droppedByRules > 0 && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', borderRadius: 6, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>
          {t('sp_rules_hide_hotels', 'Filter rules currently hide {count} hotels that fall in your radius. Loosen the rules above to widen.', { count: droppedByRules })}
        </div>
      )}

      {/* Curation-status filter — show only Preferred / Included / Excluded hotels */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: '#64748b', marginRight: 2 }}>{t('sp_show_label', 'Show:')}</span>
        {[
          ['all', t('sp_filter_all', 'All')],
          ['preferred', t('sp_rule_preferred', 'Preferred')],
          ['include', t('sp_rule_included', 'Included')],
          ['exclude', t('sp_rule_excluded', 'Excluded')],
        ].map(([val, label]) => {
          const active = ruleStatusFilter === val
          const count = val === 'all' ? hotels.length : hotels.filter(h => (h.listType || null) === val).length
          return (
            <button key={val} type="button" onClick={() => setRuleStatusFilter(val)}
              style={{
                padding: '5px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                border: '1px solid ' + (active ? '#0f172a' : '#e2e8f0'),
                background: active ? '#0f172a' : '#fff',
                color: active ? '#fff' : '#475569',
              }}>
              {label}{val !== 'all' && <span style={{ opacity: 0.7 }}> {count}</span>}
            </button>
          )
        })}
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <input
          placeholder={t('sp_filter_name_city_placeholder', 'Filter by name or city…')}
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ ...inp, width: 260 }}
        />
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#64748b' }}>
          {t('sp_min_stars', 'Min stars:')}
          <select value={minStars} onChange={e => setMinStars(Number(e.target.value))} style={{ ...inp, padding: '6px 10px' }}>
            <option value={0}>{t('sp_stars_any', 'Any')}</option>
            <option value={2}>2+</option>
            <option value={3}>3+</option>
            <option value={4}>4+</option>
            <option value={5}>5</option>
          </select>
        </label>
        <button onClick={load} disabled={loading} style={secondaryBtn}>
          {loading ? t('sp_loading_short', 'Loading…') : t('sp_refresh', 'Refresh')}
        </button>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#64748b' }}>
          {t('sp_sort', 'Sort:')}
          <select
            value={sortMode}
            onChange={e => setSortMode(e.target.value)}
            style={{ ...inp, padding: '6px 10px' }}
          >
            <option value="rating">{t('sp_sort_rating', 'Star & guest rating (best first)')}</option>
            <option value="alpha">{t('sp_sort_alpha', 'Alphabetical (A–Z)')}</option>
            <option value="distance">{t('sp_sort_distance', 'Nearest to center')}</option>
            {withRates && <option value="price-asc">{t('sp_sort_price_low', 'Price (low to high)')}</option>}
            {withRates && <option value="price-desc">{t('sp_sort_price_high', 'Price (high to low)')}</option>}
          </select>
        </label>
        {cached && <span style={{ fontSize: 11, color: '#94a3b8' }}>{t('sp_cached_30_min', 'cached (30 min)')}</span>}
        <label
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#475569', cursor: 'pointer' }}
          title={t('sp_rates_toggle_title', 'Layer a sample-stay rate lookup on each hotel so you can see approximate nightly cost. One extra API call per refresh.')}
        >
          <input
            type="checkbox"
            checked={withRates}
            onChange={e => setWithRates(e.target.checked)}
            disabled={loading}
          />
          {t('sp_show_estimated_rates', 'Show estimated nightly rates')}
        </label>
        {withRates && rateLookup && !rateLookup.error && (
          <span style={{ fontSize: 11, color: '#94a3b8' }}>
            {t('sp_rate_sample_summary', 'sample: {checkin} → {checkout} · {adults} adults, {rooms} room · {priced} of {total} priced', { checkin: rateLookup.checkin, checkout: rateLookup.checkout, adults: rateLookup.adults, rooms: rateLookup.rooms, priced: rateLookup.withRatesFor || 0, total: hotels.length })}
          </span>
        )}
        {withRates && rateLookup?.error && (
          <span style={{ fontSize: 11, color: '#b45309' }}>
            {t('sp_rate_lookup_failed', 'rate lookup failed — content only')}
          </span>
        )}
        {/* Price range filters — only relevant when withRates is on, since
            we can't filter on a price we don't have. Empty input on either
            side = no bound that direction. Hotels without a fetched rate
            get filtered out as soon as either bound is set. */}
        {withRates && (
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#475569' }}>
            {t('sp_per_night_label', '$/night:')}
            <input
              type="number"
              min="0"
              step="10"
              placeholder={t('sp_grouping_price_min', 'min')}
              value={minPrice}
              onChange={e => setMinPrice(e.target.value)}
              style={{ ...inp, padding: '6px 8px', width: 76 }}
              title={t('sp_hide_below_rate', 'Hide hotels priced below this nightly rate')}
            />
            <span style={{ color: '#94a3b8' }}>–</span>
            <input
              type="number"
              min="0"
              step="10"
              placeholder={t('sp_grouping_price_max', 'max')}
              value={maxPrice}
              onChange={e => setMaxPrice(e.target.value)}
              style={{ ...inp, padding: '6px 8px', width: 76 }}
              title={t('sp_hide_above_rate', 'Hide hotels priced above this nightly rate')}
            />
            {(minPrice !== '' || maxPrice !== '') && (
              <button
                type="button"
                onClick={() => { setMinPrice(''); setMaxPrice('') }}
                style={{
                  padding: '2px 8px', fontSize: 11, fontWeight: 600,
                  background: '#fff', color: '#475569',
                  border: '1px solid #cbd5e1', borderRadius: 4,
                  cursor: 'pointer',
                }}
              >{t('sp_clear', 'Clear')}</button>
            )}
          </label>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 13, color: '#64748b' }}>
          {droppedByRules > 0
            ? t('sp_x_of_y_hotels_match', '{matched} of {total} hotels match', { matched: ruleFiltered.length, total: hotels.length })
            : t('sp_x_hotels_in_radius', '{count} hotels in radius', { count: hotels.length })}
        </span>
      </div>

      {/* Always-visible bulk-action bar.
          Reseller workflow: a 100-km radius around a city usually pulls 200-500
          hotels — too many for one-by-one curation. Most resellers exclude the
          entire set first and then re-include the dozen they want to surface.
          Surfacing the bulk verbs at the top (not buried in the Filter rules
          card) makes that workflow one click. Count tracks the CURRENT visible
          list so filters + the name/city search still narrow what gets touched. */}
      {visible.length > 0 && (
        <div style={{
          display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
          padding: '10px 14px', marginBottom: 12,
          background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
        }}>
          <span style={{ fontSize: 12, color: '#475569', fontWeight: 600 }}>
            {t('sp_bulk_action_applies', 'Bulk action — applies to the {count} locations below:', { count: visible.length })}
          </span>
          <button
            type="button"
            disabled={bulkBusy || visible.length === 0}
            onClick={() => bulkApply('preferred')}
            style={bulkBtn('#00d19f', bulkBusy)}
          >
            {t('sp_mark_x_as_preferred', 'Mark {count} locations as Preferred', { count: visible.length })}
          </button>
          <button
            type="button"
            disabled={bulkBusy || visible.length === 0}
            onClick={() => bulkApply('include')}
            style={bulkBtn('#3b82f6', bulkBusy)}
          >
            {t('sp_mark_x_as_included', 'Mark {count} as Included', { count: visible.length })}
          </button>
          <button
            type="button"
            disabled={bulkBusy || visible.length === 0}
            onClick={() => bulkApply('exclude')}
            style={bulkBtn('#dc2626', bulkBusy)}
          >
            {t('sp_mark_x_as_excluded', 'Mark {count} as Excluded', { count: visible.length })}
          </button>
          <button
            type="button"
            disabled={bulkBusy || visible.length === 0}
            onClick={() => bulkApply('reset')}
            style={{ ...bulkBtn('#64748b', bulkBusy), marginLeft: 'auto' }}
            title={t('sp_reset_override_title', 'Remove any per-hotel override (returns to default Include)')}
          >
            {t('reset_to_default', 'Reset to Default')}
          </button>
        </div>
      )}

      {error && <div style={{ padding: 12, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6, marginBottom: 16 }}>{error}</div>}

      {loading ? (
        <div style={empty}>{t('sp_searching_expedia', 'Searching for hotels…')}</div>
      ) : visible.length === 0 ? (
        <div style={empty}>{hotels.length === 0 ? t('sp_no_hotels_matching_filter', 'No hotels in radius matching the filter.') : t('sp_no_matches_for_search', 'No matches for that search.')}</div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 12 }}>
            {paged.map(h => {
              const dKm = hotelDistanceKm(h)
              return (
                <HotelCard
                  key={`${h.source || 'expedia'}:${h.externalId}`}
                  hotel={h}
                  distanceKm={Number.isFinite(dKm) ? dKm : null}
                  busy={savingId === `${h.source || 'expedia'}:${h.externalId}`}
                  onSetListType={(lt) => setListType(h, lt)}
                />
              )
            })}
          </div>
          <HotelsPager
            startIdx={pageStartIdx}
            endIdx={pageEndIdx}
            total={totalFiltered}
            page={safePage}
            totalPages={totalPages}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </>
      )}
    </>
  )
}

/**
 * Pager for the hotels grid. Same shape as the one on the admin Course
 * Integrations page — page-size selector + condensed page numbers, hides
 * itself when the visible set fits in a single page so the UI stays
 * uncluttered for small radii.
 */
function HotelsPager({ startIdx, endIdx, total, page, totalPages, pageSize, onPageChange, onPageSizeChange }) {
  const { t } = useLanguage()
  function pageNumbers() {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1)
    const out = new Set([1, totalPages, page, page - 1, page + 1])
    const sorted = Array.from(out).filter(n => n >= 1 && n <= totalPages).sort((a, b) => a - b)
    const result = []
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push('…')
      result.push(sorted[i])
    }
    return result
  }
  const showPager = pageSize !== 'all' && totalPages > 1
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 12, padding: '12px 14px', marginTop: 16,
      background: '#f8fafc', border: '1px solid #e2e8f0',
      borderRadius: 8, fontSize: 13, color: '#475569', flexWrap: 'wrap',
    }}>
      <div>
        {t('sp_showing_range_of_total', 'Showing {start}–{end} of {total}', { start: total === 0 ? 0 : startIdx, end: endIdx, total })}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {t('sp_per_page', 'Per page:')}
          <select
            value={String(pageSize)}
            onChange={e => onPageSizeChange(e.target.value === 'all' ? 'all' : Number(e.target.value))}
            style={{ padding: '4px 8px', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 13, background: '#fff', color: '#0f172a' }}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="all">{t('all', 'All')}</option>
          </select>
        </label>
        {showPager && (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <HotelsPagerBtn onClick={() => onPageChange(page - 1)} disabled={page <= 1}>{t('sp_prev', '‹ Prev')}</HotelsPagerBtn>
            {pageNumbers().map((n, i) => (
              n === '…'
                ? <span key={`gap-${i}`} style={{ padding: '0 6px', color: '#94a3b8' }}>…</span>
                : <HotelsPagerBtn key={n} active={n === page} onClick={() => onPageChange(n)}>{n}</HotelsPagerBtn>
            ))}
            <HotelsPagerBtn onClick={() => onPageChange(page + 1)} disabled={page >= totalPages}>{t('sp_next', 'Next ›')}</HotelsPagerBtn>
          </div>
        )}
      </div>
    </div>
  )
}
function HotelsPagerBtn({ active, disabled, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        minWidth: 30, padding: '4px 10px',
        border: active ? '1px solid #00d19f' : '1px solid #cbd5e1',
        background: active ? '#00d19f' : '#fff',
        color: active ? '#fff' : (disabled ? '#cbd5e1' : '#0f172a'),
        borderRadius: 4, fontSize: 13, fontWeight: 500,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >{children}</button>
  )
}

// ─── Filter rules card + preview helper ────────────────────────────────
//
// Mirror of backend src/services/hotel/filterRules.js — keep the logic
// in sync so what the reseller sees in the portal matches what the
// customer iframe shows. The card lets the reseller flip every tier-1/2
// knob without having to know which tier each one is.

const HOTEL_CATEGORIES = [
  'Hotel', 'Resort', 'Motel', 'Bed & Breakfast', 'Hostel', 'Apartment',
  'Condo', 'Cabin', 'Vacation Rental', 'Inn',
]
const HOTEL_AMENITIES = [
  'Wi-Fi', 'Parking (free)', 'Parking (paid)', 'Breakfast included',
  'All-inclusive', 'Restaurant', 'Bar / Lounge', 'Pool', 'Hot tub', 'Spa',
  'Gym / Fitness', 'Golf course on-site', 'Tennis', 'Beach', 'Ski-in / ski-out',
  'Pet-friendly', 'Family-friendly', 'Business center', 'Meeting rooms',
  'Accessible (wheelchair)', 'EV charging', 'Kitchen', '24-hour front desk',
  'Laundry', 'Concierge',
]
const HOTEL_THEMES = [
  'Family-friendly', 'Romantic', 'Luxury', 'Boutique', 'Budget',
  'Beach', 'Ski', 'Casino', 'All-inclusive', 'Spa', 'Historic',
  'Adults-only', 'Eco-friendly', 'Business',
]
const HOTEL_BRANDS = [
  'Marriott', 'Hilton', 'IHG', 'Wyndham', 'Hyatt', 'Accor', 'Best Western',
  'Choice Hotels', 'Radisson', 'Four Seasons', 'Fairmont', 'Independent',
]

// Same logic + leniency as backend filterRules.applyFilters. Lenient =
// when a hotel lacks the field we'd filter on (older cached row),
// pass it through rather than drop. Avoids the "every filter hides
// everything" result customers were hitting before the cache was
// extended to store amenities / themes / brand / category.
// Mirrors backend src/services/hotel/filterRules.js — chip labels →
// real Expedia substrings (e.g. "Wi-Fi" → "Free WiFi", "Pool" → matches
// "Number of indoor pools - 1" but excludes "Pool nearby").
const AMENITY_ALIASES = {
  'wi-fi':              { include: ['wifi', 'wireless internet', 'wired internet'] },
  'parking (free)':     { include: ['free self parking', 'free valet parking', 'free parking'] },
  'parking (paid)':     { include: ['self parking (surcharge)', 'valet parking (surcharge)', 'covered parking', 'uncovered parking'] },
  'breakfast included': { include: ['free breakfast', 'breakfast included'] },
  'all-inclusive':      { include: ['all-inclusive', 'all inclusive'] },
  'restaurant':         { include: ['restaurant', 'snack bar', 'coffee shop'] },
  'bar / lounge':       { include: ['bar', 'lounge', 'pub'] },
  'pool':               { include: ['pool'], excludeIf: ['nearby', 'no pool'] },
  'hot tub':            { include: ['hot tub', 'spa tub', 'jacuzzi'] },
  'spa':                { include: ['spa services', 'full-service spa', 'spa on site', 'spa on-site', 'health or beauty spa'] },
  'gym / fitness':      { include: ['fitness', 'gym', 'health club'] },
  'golf course on-site':{ include: ['golf'], excludeIf: ['nearby'] },
  'tennis':             { include: ['tennis'], excludeIf: ['nearby'] },
  'beach':              { include: ['beach'], excludeIf: ['nearby'] },
  'ski-in / ski-out':   { include: ['ski-in', 'ski/out', 'ski in/ski out'] },
  'pet-friendly':       { include: ['pet'], excludeIf: ['no pets'] },
  'family-friendly':    { include: ['family', 'kids', "children's"] },
  'business center':    { include: ['business center'] },
  'meeting rooms':      { include: ['meeting rooms', 'conference space', 'banquet'] },
  'accessible (wheelchair)': { include: ['wheelchair accessible'], excludeIf: ['– no', '- no'] },
  'ev charging':        { include: ['electric car charging', 'ev charging'] },
  'kitchen':            { include: ['kitchen'] },
  '24-hour front desk': { include: ['24-hour front desk'] },
  'laundry':            { include: ['laundry'] },
  'concierge':          { include: ['concierge'] },
}
const THEME_ALIASES = {
  'family-friendly': ['family property', 'family'],
  'romantic':        ['romantic'],
  'luxury':          ['luxury property', 'luxury'],
  'boutique':        ['boutique property', 'boutique'],
  'budget':          ['budget'],
  'beach':           ['beach property', 'beach'],
  'ski':             ['ski property', 'ski'],
  'casino':          ['casino property', 'casino'],
  'all-inclusive':   ['all-inclusive', 'all inclusive'],
  'spa':             ['spa property', 'spa'],
  'historic':        ['historic property', 'historic'],
  'adults-only':     ['adults only', 'adults-only'],
  'eco-friendly':    ['eco-certified property', 'eco'],
  'business':        ['business property', 'business'],
}
const BRAND_ALIASES = {
  'marriott':       ['marriott', 'courtyard', 'residence inn', 'fairfield inn', 'springhill suites', 'townplace', 'ac hotel', 'aloft', 'autograph', 'delta hotels', 'le méridien', 'renaissance', 'ritz-carlton', 'sheraton', 'st. regis', 'westin', 'w hotels'],
  'hilton':         ['hilton', 'doubletree', 'embassy suites', 'hampton', 'home2 suites', 'homewood', 'tru by hilton', 'curio', 'canopy', 'tapestry', 'waldorf'],
  'ihg':            ['ihg', 'holiday inn', 'crowne plaza', 'intercontinental', 'staybridge', 'candlewood', 'kimpton', 'hotel indigo', 'avid'],
  'wyndham':        ['wyndham', 'days inn', 'super 8', 'ramada', 'travelodge', 'howard johnson', 'la quinta', 'baymont', 'microtel', 'hawthorn'],
  'hyatt':          ['hyatt', 'andaz', 'thompson', 'park hyatt', 'grand hyatt'],
  'accor':          ['accor', 'sofitel', 'novotel', 'mercure', 'pullman', 'ibis', 'fairmont', 'raffles', 'mövenpick'],
  'best western':   ['best western'],
  'choice hotels':  ['choice', 'comfort inn', 'comfort suites', 'quality inn', 'sleep inn', 'clarion', 'econo lodge', 'rodeway', 'mainstay', 'ascend collection', 'cambria'],
  'radisson':       ['radisson', 'park inn', 'country inn'],
  'four seasons':   ['four seasons'],
  'fairmont':       ['fairmont'],
  'independent':    ['independent'],
}
function aliasMatch(strings, label, aliases) {
  const def = aliases[String(label || '').trim().toLowerCase()]
  if (!def) return strings.some(s => String(s || '').toLowerCase().includes(String(label || '').toLowerCase()))
  const inc = Array.isArray(def) ? def : (def.include || [])
  const exc = Array.isArray(def) ? []  : (def.excludeIf || [])
  return strings.some(s => {
    const sl = String(s || '').toLowerCase()
    if (!inc.some(i => sl.includes(i.toLowerCase()))) return false
    if (exc.some(e => sl.includes(e.toLowerCase()))) return false
    return true
  })
}

function applyRulesPreview(hotels, rules) {
  if (!Array.isArray(hotels) || !rules) return hotels || []
  const eq = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase()
  return hotels.filter(h => {
    if (rules.minStarRating != null && h.starRating != null && Number(h.starRating) < Number(rules.minStarRating)) return false
    if (rules.minGuestRating != null && h.guestRating != null && Number(h.guestRating) < Number(rules.minGuestRating)) return false
    if (rules.minReviewCount != null && Number(h.guestReviews || h.guestReviewCount || 0) < Number(rules.minReviewCount)) return false
    if (Array.isArray(rules.propertyCategories) && rules.propertyCategories.length) {
      const cat = h.category || h.businessModel || null
      if (cat) {
        // Substring match so chip "Hostel" hits "Hostel/Backpacker accommodation",
        // chip "Bed & Breakfast" hits "Bed & breakfast", etc.
        const cl = String(cat).toLowerCase()
        const ok = rules.propertyCategories.some(c => cl.includes(String(c).toLowerCase()) || String(c).toLowerCase().includes(cl))
        if (!ok) return false
      }
    }
    if (Array.isArray(rules.requiredAmenities) && rules.requiredAmenities.length) {
      const names = (h.amenities || []).map(a => typeof a === 'string' ? a : a?.name).filter(Boolean)
      if (names.length > 0) {
        for (const need of rules.requiredAmenities) if (!aliasMatch(names, need, AMENITY_ALIASES)) return false
      }
    }
    if (Array.isArray(rules.themes) && rules.themes.length) {
      const t = (h.themes || []).map(x => typeof x === 'string' ? x : x?.name).filter(Boolean)
      if (t.length > 0) {
        const ok = rules.themes.some(r => aliasMatch(t, r, THEME_ALIASES))
        if (!ok) return false
      }
    }
    const brand = h.brand || h.chain || null
    if (Array.isArray(rules.allowedBrands) && rules.allowedBrands.length && brand) {
      if (!rules.allowedBrands.some(b => aliasMatch([brand], b, BRAND_ALIASES))) return false
    }
    if (Array.isArray(rules.excludedBrands) && rules.excludedBrands.length && brand) {
      if (rules.excludedBrands.some(b => aliasMatch([brand], b, BRAND_ALIASES))) return false
    }
    if (h.allInclusive === true && rules.excludeAllInclusive) return false
    if (h.allInclusive === false && rules.onlyAllInclusive) return false
    if (rules.onlyAllInclusive && h.allInclusive == null) return false
    return true
  })
}

function FilterRulesCard({ rules, onChange, saving, matchCount, onBulkApply, bulkBusy }) {
  const { t } = useLanguage()
  const [open, setOpen] = useState(false)
  function set(k, v) { onChange({ ...rules, [k]: v }) }
  function toggleInList(k, value) {
    const cur = Array.isArray(rules[k]) ? rules[k] : []
    const has = cur.some(x => String(x).toLowerCase() === String(value).toLowerCase())
    set(k, has ? cur.filter(x => String(x).toLowerCase() !== String(value).toLowerCase()) : [...cur, value])
  }

  // Cheap "active" badge — count how many rules are non-default.
  const activeCount = [
    rules.minStarRating, rules.minGuestRating, rules.minReviewCount,
    rules.propertyCategories?.length, rules.requiredAmenities?.length,
    rules.themes?.length, rules.allowedBrands?.length, rules.excludedBrands?.length,
    rules.refundableOnly, rules.onlyAllInclusive, rules.excludeAllInclusive,
  ].filter(Boolean).length

  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 16, overflow: 'hidden' }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{ width: '100%', padding: 14, textAlign: 'left', background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}
      >
        <span style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{t('sp_filter_rules', 'Filter rules')}</span>
        {activeCount > 0 && (
          <span style={{ fontSize: 11, padding: '2px 8px', background: '#0f172a', color: '#fff', borderRadius: 999, fontWeight: 600 }}>{t('sp_x_active', '{count} active', { count: activeCount })}</span>
        )}
        {saving && <span style={{ fontSize: 11, color: '#64748b' }}>{t('sp_saving_lower', 'saving…')}</span>}
        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          {activeCount > 0 && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onChange({}) }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); onChange({}) } }}
              style={{ fontSize: 12, color: '#dc2626', cursor: 'pointer', textDecoration: 'underline', fontWeight: 500 }}
            >
              {t('sp_grouping_clear_all', 'Clear all')}
            </span>
          )}
          <span style={{ fontSize: 14, color: '#64748b' }}>{open ? '▾' : '▸'}</span>
        </span>
      </button>

      {open && (
        <div style={{ borderTop: '1px solid #e2e8f0', padding: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>

          <div>
            <Label>{t('sp_min_star_rating', 'Min star rating')}</Label>
            <select value={rules.minStarRating || ''} onChange={e => set('minStarRating', e.target.value ? Number(e.target.value) : null)} style={inp}>
              <option value="">{t('sp_no_minimum', 'No minimum')}</option>
              <option value="2">2+</option>
              <option value="3">3+</option>
              <option value="3.5">3.5+</option>
              <option value="4">4+</option>
              <option value="4.5">4.5+</option>
              <option value="5">5</option>
            </select>
          </div>
          <div>
            <Label>{t('sp_min_guest_rating', 'Min guest rating')}</Label>
            <select value={rules.minGuestRating || ''} onChange={e => set('minGuestRating', e.target.value ? Number(e.target.value) : null)} style={inp}>
              <option value="">{t('sp_no_minimum', 'No minimum')}</option>
              <option value="3">3.0+</option>
              <option value="3.5">3.5+</option>
              <option value="4">4.0+</option>
              <option value="4.5">4.5+</option>
            </select>
          </div>
          <div>
            <Label>{t('sp_min_review_count', 'Min review count')}</Label>
            <input type="number" min={0} step={5}
              value={rules.minReviewCount ?? ''}
              onChange={e => set('minReviewCount', e.target.value === '' ? null : Number(e.target.value))}
              placeholder={t('sp_review_count_placeholder', 'any')} style={inp} />
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <Label>{t('sp_property_categories', 'Property categories')} <span style={{ color: '#94a3b8', fontWeight: 400 }}>{t('sp_allow_only_empty_all', '(allow only — empty = all)')}</span></Label>
            <ChipSet options={HOTEL_CATEGORIES} value={rules.propertyCategories || []}
              onToggle={v => toggleInList('propertyCategories', v)} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <Label>{t('sp_required_amenities', 'Required amenities')} <span style={{ color: '#94a3b8', fontWeight: 400 }}>{t('sp_must_have_all', '(must have ALL)')}</span></Label>
            <ChipSet options={HOTEL_AMENITIES} value={rules.requiredAmenities || []}
              onToggle={v => toggleInList('requiredAmenities', v)} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <Label>{t('sp_themes', 'Themes')} <span style={{ color: '#94a3b8', fontWeight: 400 }}>{t('sp_at_least_one_match', '(at least one match)')}</span></Label>
            <ChipSet options={HOTEL_THEMES} value={rules.themes || []}
              onToggle={v => toggleInList('themes', v)} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <Label>{t('sp_allowed_brands', 'Allowed brands')} <span style={{ color: '#94a3b8', fontWeight: 400 }}>{t('sp_only_these_empty_all', '(only these — empty = all)')}</span></Label>
            <ChipSet options={HOTEL_BRANDS} value={rules.allowedBrands || []}
              onToggle={v => toggleInList('allowedBrands', v)} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <Label>{t('sp_excluded_brands', 'Excluded brands')} <span style={{ color: '#94a3b8', fontWeight: 400 }}>{t('sp_never_these', '(never these)')}</span></Label>
            <ChipSet options={HOTEL_BRANDS} value={rules.excludedBrands || []}
              onToggle={v => toggleInList('excludedBrands', v)} />
          </div>

          <div>
            <ToggleRow label={t('sp_refundable_rates_only', 'Refundable rates only')} checked={!!rules.refundableOnly} onChange={v => set('refundableOnly', v)} />
          </div>
          <div>
            <ToggleRow label={t('sp_exclude_all_inclusive', 'Exclude all-inclusive')} checked={!!rules.excludeAllInclusive} onChange={v => set('excludeAllInclusive', v)} />
          </div>
          <div>
            <ToggleRow label={t('sp_only_all_inclusive', 'Only all-inclusive')} checked={!!rules.onlyAllInclusive} onChange={v => set('onlyAllInclusive', v)} />
          </div>

          {/* Bulk-apply footer — only meaningful when filters are active.
              Without filters, "apply to all hotels in radius" would be too
              broad to ever want as a one-click action. The match count
              makes it crystal-clear how many rows will be touched. */}
          {activeCount > 0 && onBulkApply && (
            <div style={{ gridColumn: '1 / -1', borderTop: '1px solid #e2e8f0', marginTop: 4, paddingTop: 14 }}>
              <div style={{ fontSize: 12, color: '#475569', marginBottom: 10 }}>
                {t('sp_apply_to_matching', 'Apply to the {count} hotels matching the filters above:', { count: matchCount })}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  disabled={bulkBusy || !matchCount}
                  onClick={() => onBulkApply('preferred')}
                  style={bulkBtn('#00d19f', bulkBusy)}
                >
                  {t('sp_mark_as_preferred', 'Mark as Preferred')}
                </button>
                <button
                  type="button"
                  disabled={bulkBusy || !matchCount}
                  onClick={() => onBulkApply('include')}
                  style={bulkBtn('#3b82f6', bulkBusy)}
                >
                  {t('sp_mark_as_included', 'Mark as Included')}
                </button>
                <button
                  type="button"
                  disabled={bulkBusy || !matchCount}
                  onClick={() => onBulkApply('exclude')}
                  style={bulkBtn('#dc2626', bulkBusy)}
                >
                  {t('sp_mark_as_excluded', 'Mark as Excluded')}
                </button>
                <button
                  type="button"
                  disabled={bulkBusy || !matchCount}
                  onClick={() => onBulkApply('reset')}
                  style={{ ...bulkBtn('#64748b', bulkBusy), marginLeft: 'auto' }}
                  title={t('sp_reset_override_title', 'Remove any per-hotel override (returns to default Include)')}
                >
                  {t('reset_to_default', 'Reset to Default')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function bulkBtn(color, busy) {
  return {
    padding: '8px 14px', fontSize: 12, fontWeight: 600,
    background: '#fff', color, border: `1px solid ${color}`, borderRadius: 6,
    cursor: busy ? 'wait' : 'pointer',
    opacity: busy ? 0.6 : 1,
  }
}

function Label({ children }) {
  return <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>{children}</div>
}
function ChipSet({ options, value, onToggle }) {
  const lower = (value || []).map(v => String(v).toLowerCase())
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {options.map(opt => {
        const active = lower.includes(String(opt).toLowerCase())
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onToggle(opt)}
            style={{
              padding: '5px 10px',
              fontSize: 12,
              background: active ? '#0f172a' : '#fff',
              color: active ? '#fff' : '#475569',
              border: '1px solid ' + (active ? '#0f172a' : '#e2e8f0'),
              borderRadius: 999,
              cursor: 'pointer',
              fontWeight: active ? 600 : 400,
            }}
          >
            {active && '✓ '}{opt}
          </button>
        )
      })}
    </div>
  )
}
function ToggleRow({ label, checked, onChange }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
      <Toggle checked={checked} onChange={onChange} />
      <span>{label}</span>
    </label>
  )
}

function HotelCard({ hotel: h, busy, onSetListType, distanceKm = null }) {
  const { t } = useLanguage()
  // No row in reseller_grouping_hotel_lists = treated as "Include" by default
  // (every hotel in radius is shown to customers unless explicitly excluded).
  // Visual reflects this: the Include button shows checked + the card has a
  // soft tint so the reseller can tell at a glance "this one is in".
  const effective = h.listType || 'include'
  const tint =
    effective === 'preferred' ? '#ecfdf5' :
    effective === 'exclude'   ? '#fef2f2' :
    effective === 'include'   ? '#eff6ff' : '#fff'
  const borderColor =
    effective === 'preferred' ? '#00d19f' :
    effective === 'exclude'   ? '#dc2626' :
    effective === 'include'   ? '#3b82f6' : '#e2e8f0'
  return (
    <div style={{
      background: tint, border: `2px solid ${borderColor}`, borderRadius: 8, overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
    }}>
      {h.image ? (
        <div style={{ height: 140, backgroundImage: `url(${h.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
      ) : (
        <div style={{ height: 140, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1', fontSize: 12 }}>{t('sp_no_photo', 'no photo')}</div>
      )}
      <div style={{ padding: 12, flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
          <strong style={{ fontSize: 14, color: '#0f172a', lineHeight: 1.3 }}>{h.name}</strong>
          {h.starRating != null && (
            <span style={{ fontSize: 12, color: '#f59e0b', whiteSpace: 'nowrap' }}>
              {'★'.repeat(Math.round(h.starRating))}<span style={{ color: '#cbd5e1' }}>{'★'.repeat(5 - Math.round(h.starRating))}</span>
            </span>
          )}
        </div>
        <div style={{ fontSize: 12, color: '#64748b' }}>
          {[h.line1, h.city, h.stateProvince].filter(Boolean).join(', ')}
        </div>
        {distanceKm != null && (
          <div style={{ fontSize: 11, color: '#475569', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
            {t('sp_km_from_center', '{km} km from center', { km: distanceKm < 10 ? distanceKm.toFixed(1) : Math.round(distanceKm) })}
          </div>
        )}
        {h.guestRating != null && (
          <div style={{ fontSize: 11, color: '#475569' }}>
            {t('sp_guest_rating_reviews', 'Guest {rating}/5 · {count} reviews', { rating: h.guestRating, count: h.guestReviews })}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <ProviderBadge provider={h.source} size="xs" />
          <span style={{ fontSize: 10, color: '#94a3b8', fontFamily: 'monospace' }}>#{h.externalId}</span>
        </div>
        {/* Estimated nightly rate from sample-date Expedia search (includes
            InteGolf hotel markup). Only renders when the reseller has
            opted in via the "Show estimated nightly rates" toggle AND
            Expedia returned availability for this property on the
            sample window. Customer-facing rates re-quote per package
            at booking time so this is portal preview only. */}
        {h.cheapestNightly != null && (
          <div style={{
            marginTop: 2, alignSelf: 'flex-start',
            padding: '3px 8px',
            background: '#f0fdf4', color: '#15803d',
            border: '1px solid #86efac',
            borderRadius: 999,
            fontSize: 11, fontWeight: 600,
          }}
          title={t('sp_nightly_rate_title', 'Estimated nightly rate based on a sample 2-night stay 30 days out — includes InteGolf hotel markup. Customer-facing prices re-quote per package at booking.')}>
            {t('sp_from_per_night', 'from ${amount} / night', { amount: Number(h.cheapestNightly).toFixed(2) })}
          </div>
        )}
        <div style={{ display: 'flex', gap: 4, marginTop: 'auto', paddingTop: 8 }}>
          {[
            { val: 'preferred', color: '#00d19f', label: t('sp_list_preferred', 'Preferred') },
            { val: 'include',   color: '#3b82f6', label: t('sp_list_include', 'Include') },
            { val: 'exclude',   color: '#dc2626', label: t('sp_list_exclude', 'Exclude') },
          ].map(opt => {
            const checked = effective === opt.val
            return (
              <button
                key={opt.val}
                onClick={() => onSetListType(h.listType === opt.val ? null : opt.val)}
                disabled={busy}
                style={{
                  flex: 1, padding: '6px 4px', fontSize: 11, fontWeight: 600,
                  background: checked ? opt.color : '#fff',
                  color: checked ? '#fff' : opt.color,
                  border: `1px solid ${opt.color}`, borderRadius: 4,
                  cursor: busy ? 'wait' : 'pointer',
                }}
              >
                {checked ? '✓ ' : ''}{opt.label}
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ─── Packages tab ───────────────────────────────────────────────────────
//
// Packages are scoped to a grouping (reseller_packages.Grouping_ID), so this
// tab fetches/creates them in the context of the current grouping. Editing a
// package still navigates to the standalone /portal/reseller/packages/:id
// page since that has the multi-tab editor (basic/courses/play-rules/addons/refund).

function PackagesTab({ grouping }) {
  const { t } = useLanguage()
  const navigate = useNavigate()
  const [packages, setPackages] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [newName, setNewName] = useState('')
  const [creating, setCreating] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [seeding, setSeeding] = useState(false)
  // Activation readiness — same checklist shown on the package editor.
  // Surfaced here too so the reseller knows what's blocking Active
  // before they even open a package.
  const [activation, setActivation] = useState(null)

  function reload() {
    setLoading(true); setError(null)
    tournamentApi.get('/portal/reseller/packages', { params: { groupingId: grouping.ID_NO } })
      .then(r => setPackages(r.data?.packages || []))
      .catch(e => setError(e?.response?.data?.message || e.message))
      .finally(() => setLoading(false))
    tournamentApi.get('/portal/reseller/packages/activation-readiness')
      .then(r => setActivation(r.data))
      .catch(() => { /* non-fatal */ })
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload() }, [grouping.ID_NO])

  async function create(e) {
    e.preventDefault()
    if (!newName.trim() || creating) return
    setCreating(true)
    try {
      const { data } = await tournamentApi.post('/portal/reseller/packages', {
        groupingId: grouping.ID_NO,
        name: newName.trim(),
      })
      navigate(`/portal/reseller/packages/${data.packageId}`)
    } catch (e2) {
      alert(e2?.response?.data?.message || e2.message)
      setCreating(false)
    }
  }

  async function seedTemplates() {
    if (!confirm(t('sp_seed_templates_confirm', 'Create 3 starter packages (Classic Golf Package · Signature Golf Package · Premier Golf Package)? You can edit or delete them after.'))) return
    setSeeding(true)
    try {
      await tournamentApi.post('/portal/reseller/packages/seed-templates', { groupingId: grouping.ID_NO })
      reload()
    } catch (e) {
      alert(e?.response?.data?.message || e.message)
    } finally { setSeeding(false) }
  }

  return (
    <>
      <ActivationChecklist activation={activation} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 16 }}>
        <p style={{ color: '#64748b', maxWidth: 640, margin: 0, fontSize: 14, lineHeight: 1.5 }}>
          {t('sp_packages_intro', 'Packages live inside this grouping. Each one is a sellable trip — set group size, nights/rooms, per-course max prices, multi-course play rules, add-ons, and refund tiers. Click a package to open the editor.')}
        </p>
        <button onClick={() => setShowNew(s => !s)} style={primaryBtn}>
          {showNew ? t('cancel', 'Cancel') : t('sp_new_package_btn', '+ New package')}
        </button>
      </div>

      {showNew && (
        <form onSubmit={create} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, marginBottom: 16, display: 'flex', gap: 10, alignItems: 'flex-end' }}>
          <Field label={t('sp_package_name', 'Package name')} style={{ flex: 1 }}>
            <input
              type="text"
              required
              autoFocus
              value={newName}
              onChange={e => setNewName(e.target.value)}
              style={inp}
              placeholder={t('sp_grouping_package_name_placeholder', '3-night {name} Stay & Play', { name: grouping.Name })}
            />
          </Field>
          <button type="submit" disabled={creating || !newName.trim()} style={{ ...primaryBtn, opacity: creating ? 0.6 : 1 }}>
            {creating ? t('sp_creating', 'Creating…') : t('sp_create_edit', 'Create + edit')}
          </button>
        </form>
      )}

      {error && <div style={{ padding: 12, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6, marginBottom: 16 }}>{error}</div>}

      {loading ? (
        <div style={empty}>{t('sp_loading_short', 'Loading…')}</div>
      ) : packages.length === 0 ? (
        <div style={{
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
          padding: 32, textAlign: 'center',
        }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#0f172a', marginBottom: 6 }}>
            {t('sp_no_packages_yet_in', 'No packages yet in {name}', { name: grouping.Name })}
          </div>
          <p style={{ color: '#64748b', fontSize: 13, lineHeight: 1.5, maxWidth: 480, margin: '0 auto 20px' }}>
            {t('sp_packages_empty_help', 'Build your own from scratch, or start with our 3 ready-made templates and edit anything after.')}
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <button
              onClick={seedTemplates}
              disabled={seeding}
              style={{ ...primaryBtn, opacity: seeding ? 0.6 : 1 }}
            >
              {seeding ? t('sp_creating', 'Creating…') : t('sp_create_3_starter_packages', 'Create 3 starter packages')}
            </button>
            <button onClick={() => setShowNew(true)} style={secondaryBtn}>
              {t('sp_build_from_scratch', 'Build from scratch')}
            </button>
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 14 }}>
            {t('sp_starter_packages_list', 'Starter packages: Classic Golf Package · Signature Golf Package · Premier Golf Package')}
          </div>
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
          <table style={tbl}>
            <thead>
              <tr style={tr}>
                <th style={th}>{t('sp_th_package', 'Package')}</th>
                <th style={th}>{t('price', 'Price')}</th>
                <th style={th}>{t('sp_grouping_group_size', 'Group size')}</th>
                <th style={th}>{t('sp_col_nights_rooms', 'Nights / Rooms')}</th>
                <th style={th}>{t('sp_col_courses_addons_tiers', 'Courses · Addons · Tiers')}</th>
                <th style={th}>{t('sp_th_featured', 'Featured')}</th>
                <th style={th}>{t('status', 'Status')}</th>
              </tr>
            </thead>
            <tbody>
              {packages.map(p => (
                <tr key={p.ID_NO} style={{ ...tr, cursor: 'pointer' }} onClick={() => navigate(`/portal/reseller/packages/${p.ID_NO}`)}>
                  <td style={td}>
                    <strong style={{ color: '#0f172a' }}>{p.Name}</strong>
                    {p.Description && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{p.Description.slice(0, 80)}{p.Description.length > 80 ? '…' : ''}</div>}
                  </td>
                  <td style={tdMono}>{p.Price != null ? `$${Number(p.Price).toFixed(2)}` : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                  <td style={tdMono}>{p.MaxGolfers || p.MinGolfers} {(p.MaxGolfers || p.MinGolfers) === 1 ? t('sp_golfer_lower', 'golfer') : t('sp_golfers_lower', 'golfers')}</td>
                  <td style={tdMono}>{p.Nights}n / {p.RoomCount}r</td>
                  <td style={tdMono}>{p.CourseCount}c · {p.AddonCount}a · {p.RefundTierCount}t</td>
                  <td style={td} onClick={e => e.stopPropagation()}>
                    <PackageFeaturedStar
                      pkgId={p.ID_NO}
                      featured={!!p.IsFeatured}
                      onToggle={next => setPackages(prev => prev.map(x =>
                        x.ID_NO === p.ID_NO
                          ? { ...x, IsFeatured: next ? 1 : 0 }
                          : { ...x, IsFeatured: next ? 0 : x.IsFeatured }))}
                    />
                  </td>
                  <td style={td} onClick={e => e.stopPropagation()}>
                    <PackageStatusSelect
                      pkgId={p.ID_NO}
                      value={p.Status}
                      onChanged={next => setPackages(prev => prev.map(x => x.ID_NO === p.ID_NO ? { ...x, Status: next } : x))}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

function PackageStatusBadge({ value }) {
  const { t } = useLanguage()
  const colors = {
    draft:    { bg: '#fef3c7', fg: '#92400e' },
    active:   { bg: '#dcfce7', fg: '#166534' },
    paused:   { bg: '#e2e8f0', fg: '#475569' },
    archived: { bg: '#f1f5f9', fg: '#94a3b8' },
  }
  const c = colors[value] || colors.draft
  return <span style={{ padding: '2px 8px', background: c.bg, color: c.fg, borderRadius: 999, fontSize: 11, fontWeight: 600 }}>{t('sp_status_' + value, value)}</span>
}

/**
 * Inline status editor — replaces the read-only badge in the packages
 * table so the reseller can flip draft ↔ active without opening the
 * editor. Stays pill-shaped (matches the badge styling) for visual
 * continuity. Cell wraps this in stopPropagation so the row-level
 * onClick (navigate to /packages/:id) doesn't fire.
 *
 * Backend already supports PUT /portal/reseller/packages/:id with a
 * partial { status } body. If activation gate fails (409
 * PAYMENT_SETUP_INCOMPLETE), surfaces the server message via alert and
 * reverts the local state.
 */
function PackageStatusSelect({ pkgId, value, onChanged }) {
  const { t } = useLanguage()
  const colors = {
    draft:    { bg: '#fef3c7', fg: '#92400e', border: '#fde68a' },
    active:   { bg: '#dcfce7', fg: '#166534', border: '#86efac' },
    paused:   { bg: '#e2e8f0', fg: '#475569', border: '#cbd5e1' },
    archived: { bg: '#f1f5f9', fg: '#94a3b8', border: '#e2e8f0' },
  }
  const c = colors[value] || colors.draft
  const [busy, setBusy] = useState(false)
  async function flip(next) {
    if (next === value) return
    setBusy(true)
    const prev = value
    onChanged(next) // optimistic
    try {
      await tournamentApi.put(`/portal/reseller/packages/${pkgId}`, { status: next })
    } catch (err) {
      onChanged(prev) // revert
      alert(err?.response?.data?.message || err?.message || t('sp_could_not_update_status', 'Could not update status.'))
    } finally { setBusy(false) }
  }
  // Plain native select with the badge color theme. Keeping the browser's
  // own dropdown arrow (no appearance:none) avoids the doubled-arrow bug
  // we saw when stacking a custom caret SVG on top of an open native list.
  return (
    <select
      value={value}
      disabled={busy}
      onChange={e => flip(e.target.value)}
      onClick={e => e.stopPropagation()}
      title={t('sp_change_status', 'Change status')}
      style={{
        padding: '3px 6px',
        background: c.bg,
        color: c.fg,
        border: `1px solid ${c.border}`,
        borderRadius: 6,
        fontSize: 12,
        fontWeight: 600,
        cursor: busy ? 'wait' : 'pointer',
      }}
    >
      <option value="draft">{t('sp_status_draft', 'draft')}</option>
      <option value="active">{t('sp_status_active', 'active')}</option>
      <option value="paused">{t('sp_status_paused', 'paused')}</option>
      <option value="archived">{t('sp_status_archived', 'archived')}</option>
    </select>
  )
}

/**
 * Featured toggle — a star in the packages table marking which package is the
 * hero of the Featured storefront layout. Only one per grouping: setting one
 * clears the rest (enforced server-side; parent clears the others locally for
 * an instant optimistic update). Click a lit star to unset (no featured).
 */
function PackageFeaturedStar({ pkgId, featured, onToggle }) {
  const { t } = useLanguage()
  const [busy, setBusy] = useState(false)
  async function toggle(e) {
    e.stopPropagation()
    const next = !featured
    setBusy(true)
    onToggle(next) // optimistic (parent clears others when next=true)
    try {
      await tournamentApi.put(`/portal/reseller/packages/${pkgId}`, { isFeatured: next })
    } catch (err) {
      onToggle(!next) // revert this row; a refresh reconciles the rest
      alert(err?.response?.data?.message || err?.message || t('sp_could_not_update_featured', 'Could not update featured.'))
    } finally { setBusy(false) }
  }
  return (
    <button
      onClick={toggle}
      disabled={busy}
      title={featured ? t('sp_featured_on', 'Featured package — click to unset') : t('sp_make_featured', 'Make this the featured package')}
      style={{
        background: 'none', border: 'none', padding: 2, lineHeight: 1,
        fontSize: 18, cursor: busy ? 'wait' : 'pointer',
        color: featured ? '#f59e0b' : '#cbd5e1',
      }}
    >
      {featured ? '★' : '☆'}
    </button>
  )
}

// ─── Branding tab ───────────────────────────────────────────────────────
//
// Per-grouping customer-facing branding. Mirrors the Stay & Play
// BrandingSetup field set: logo + 3 colors + 2 fonts + header style +
// hero image + layout. Inherits from the reseller's account brand profile
// when the grouping has nothing of its own.
//
// AI extract: pastes a URL, hits /api/course-website/extract-styles
// (the same endpoint BrandProfile uses), fills color + font fields from
// the AI response.

// 10 layout options — same set the regular Stay & Play BrandingSetup uses.
// Each value maps to a customer-iframe template the embed renderer picks.
const LAYOUT_OPTIONS = [
  { v: 'cards',      name: 'Cards',      desc: 'Equal-size grid' },
  { v: 'comparison', name: 'Comparison', desc: 'Side-by-side columns' },
  { v: 'hero',       name: 'Hero',       desc: 'Bordered cards + side price' },
  { v: 'minimal',    name: 'Minimal',    desc: 'Clean list with accent' },
  { v: 'featured',   name: 'Featured',   desc: 'One large + smaller cards' },
  { v: 'gallery',    name: 'Gallery',    desc: 'Image-first grid' },
  { v: 'carousel',   name: 'Carousel',   desc: 'Horizontal scroll strip' },
  { v: 'split',      name: 'Split',      desc: 'Alternating image/content' },
]

// Golf bundles support a focused subset of layouts — the ones that make
// sense for prepaid passes (no hotel imagery / itinerary / matrix).
const BUNDLE_LAYOUTS = ['cards', 'comparison', 'minimal', 'featured']

// Typography presets — picking one bulk-fills header/body font + weight +
// case + letter-spacing. Mirrors the S&P preset list.
const STYLE_PRESETS = [
  { v: 'inherit',   name: 'Match Site', desc: 'Use site fonts',          sample: { headerFont: '',                bodyFont: '',            headerWeight: 700, headerCase: 'normal',    headerLetterSpacing: 'normal' } },
  { v: 'classic',   name: 'Classic',    desc: 'Playfair + Lora',         sample: { headerFont: 'Playfair Display', bodyFont: 'Lora',        headerWeight: 700, headerCase: 'normal',    headerLetterSpacing: 'normal' } },
  { v: 'modern',    name: 'Modern',     desc: 'Montserrat + Open Sans',  sample: { headerFont: 'Montserrat',       bodyFont: 'Open Sans',   headerWeight: 700, headerCase: 'normal',    headerLetterSpacing: 'normal' } },
  { v: 'bold',      name: 'Bold',       desc: 'Oswald upper + Inter',    sample: { headerFont: 'Oswald',           bodyFont: 'Inter',       headerWeight: 600, headerCase: 'uppercase', headerLetterSpacing: '0.04em' } },
  { v: 'minimal',   name: 'Minimal',    desc: 'Inter / Inter, light',    sample: { headerFont: 'Inter',            bodyFont: 'Inter',       headerWeight: 500, headerCase: 'normal',    headerLetterSpacing: 'normal' } },
  { v: 'editorial', name: 'Editorial',  desc: 'Cormorant + Crimson',     sample: { headerFont: 'Cormorant Garamond', bodyFont: 'Crimson Text', headerWeight: 700, headerCase: 'normal', headerLetterSpacing: 'normal' } },
  { v: 'custom',    name: 'Custom',     desc: 'Set fonts manually',      sample: null },
]

// i18n key lookups for the preset/layout tile labels. The preset/layout
// arrays above hold the English source strings; these maps point each
// value at its existing dictionary key so t() can swap in French.
const STYLE_PRESET_NAME_KEYS = {
  inherit: 'sp_style_match_site', classic: 'sp_style_classic', modern: 'sp_style_modern',
  bold: 'sp_style_bold', minimal: 'sp_layout_minimal', editorial: 'sp_style_editorial', custom: 'sp_custom',
}
const STYLE_PRESET_DESC_KEYS = {
  inherit: 'sp_style_match_site_desc', classic: 'sp_preset_classic_desc', modern: 'sp_preset_modern_desc',
  bold: 'sp_style_bold_desc', minimal: 'sp_style_minimal_desc', editorial: 'sp_preset_editorial_desc', custom: 'sp_preset_custom_desc',
}
const LAYOUT_NAME_KEYS = {
  cards: 'sp_layout_cards', comparison: 'sp_layout_comparison', hero: 'sp_layout_hero', minimal: 'sp_layout_minimal',
  featured: 'sp_layout_featured', itinerary: 'sp_layout_itinerary', gallery: 'sp_layout_gallery',
  carousel: 'sp_layout_carousel', matrix: 'sp_layout_matrix', split: 'sp_layout_split',
}
const LAYOUT_DESC_KEYS = {
  cards: 'sp_layout_cards_desc', comparison: 'sp_layout_comparison_desc', hero: 'sp_layout_hero_desc', minimal: 'sp_layout_minimal_desc',
  featured: 'sp_layout_featured_desc', itinerary: 'sp_layout_itinerary_desc', gallery: 'sp_layout_gallery_desc',
  carousel: 'sp_layout_carousel_desc', matrix: 'sp_layout_matrix_desc', split: 'sp_layout_split_desc',
}

// Font picker fallback — same family list used by the presets, so admin
// can manually choose any of them in Custom mode.
const FONT_OPTIONS = [
  { v: '', label: 'System default' },
  'Inter', 'Playfair Display', 'Montserrat', 'Open Sans', 'Lora', 'Oswald',
  'Cormorant Garamond', 'Crimson Text', 'Roboto', 'Poppins',
  'Georgia', 'Bebas Neue', 'Source Sans Pro',
].map(f => typeof f === 'string' ? { v: f, label: f } : f)

function BrandingTab({ grouping }) {
  const { t, langId } = useLanguage()
  const isBundles = grouping?.GroupingType === 'bundles'
  const [b, setB] = useState(null)
  const [defaults, setDefaults] = useState({})
  const [packages, setPackages] = useState([])
  const [bundles, setBundles] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState(null)
  const [err, setErr] = useState(null)

  // AI extract state
  const [extractUrl, setExtractUrl] = useState('')
  const [extracting, setExtracting] = useState(false)
  const [extractErr, setExtractErr] = useState(null)
  const [extractResult, setExtractResult] = useState(null)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() }, [grouping.ID_NO])
  async function load() {
    setLoading(true); setErr(null)
    try {
      const { data } = await tournamentApi.get(`/portal/reseller/groupings/${grouping.ID_NO}/branding`)
      setDefaults(data.defaults || {})
      setPackages(data.packages || [])
      // Bundle groupings preview their own prepaid-pass cards, not S&P
      // packages — pull the real bundles so the preview is accurate.
      if (grouping.GroupingType === 'bundles') {
        try {
          const br = await tournamentApi.get('/portal/reseller/bundles', { params: { groupingId: grouping.ID_NO } })
          setBundles(br.data?.bundles || [])
        } catch { /* preview falls back to the sample bundle */ }
      }
      // Merge defaults into the form when no custom branding exists yet so
      // the colour pickers show the inherited account palette right away.
      const initial = data.branding || {
        logoUrl:               data.defaults?.logoUrl || '',
        primaryColor:          data.defaults?.primaryColor || '',
        accentColor:           '',
        secondaryColor:        data.defaults?.secondaryColor || '',
        headerFont:            '',
        bodyFont:              '',
        headerWeight:          700,
        headerCase:            'normal',
        headerLetterSpacing:   'normal',
        layout:                'cards',
        heroImageUrl:          '',
        sourceUrl:             data.defaults?.website || '',
      }
      setB(initial)
      setExtractUrl(initial.sourceUrl || data.defaults?.website || '')
    } catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setLoading(false) }
  }

  function set(k, v) { setB(prev => ({ ...prev, [k]: v })) }

  // Apply a typography preset — bulk-fills the font + weight + case fields.
  // Picking 'custom' just leaves whatever's currently set (used for manual edits
  // and AI-extracted styles).
  function applyStylePreset(presetValue) {
    const p = STYLE_PRESETS.find(s => s.v === presetValue)
    if (!p) return
    if (presetValue === 'custom') {
      // Restore extracted values if we have them, otherwise leave as-is.
      setB(prev => ({
        ...prev,
        stylePreset: 'custom',
        headerFont: prev.extractedHeaderFont || prev.headerFont,
        bodyFont:   prev.extractedBodyFont   || prev.bodyFont,
      }))
      return
    }
    setB(prev => ({
      ...prev,
      stylePreset:         presetValue,
      headerFont:          p.sample?.headerFont || '',
      bodyFont:            p.sample?.bodyFont || '',
      headerWeight:        p.sample?.headerWeight || 700,
      headerCase:          p.sample?.headerCase || 'normal',
      headerLetterSpacing: p.sample?.headerLetterSpacing || 'normal',
    }))
  }

  // Detect which preset the current state matches (for highlighting). Falls
  // back to 'custom' when nothing matches exactly. Guards b being null
  // (still loading) — the JSX below also early-returns in that case but
  // hooks above it run unconditionally each render.
  const activePreset = !b ? 'inherit' : (STYLE_PRESETS.find(p => p.sample &&
    p.sample.headerFont === (b.headerFont || '') &&
    p.sample.bodyFont   === (b.bodyFont || '') &&
    (p.sample.headerWeight || 700) === (b.headerWeight || 700) &&
    (p.sample.headerCase || 'normal') === (b.headerCase || 'normal') &&
    (p.sample.headerLetterSpacing || 'normal') === (b.headerLetterSpacing || 'normal')
  )?.v || 'custom')

  async function save() {
    setSaving(true); setErr(null); setSavedAt(null)
    try {
      await tournamentApi.put(`/portal/reseller/groupings/${grouping.ID_NO}/branding`, b)
      setSavedAt(new Date())
      setTimeout(() => setSavedAt(null), 3000)
    } catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setSaving(false) }
  }

  // Upload an image and persist it to a specific package's HeroImageUrl.
  // Used by the Hero/Gallery/Carousel/Split per-package slots. Backend
  // returns packages in S&P shape (ID_NO, HeroImageUrl, PackageName).
  async function uploadPackageImage(packageId, file) {
    if (!file) return
    try {
      const fd = new FormData()
      fd.append('image', file)
      const { data } = await tournamentApi.post('/portal/reseller/uploads/image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      // Tournament-API returns a site-relative URL like /site-assets/uploads/X.jpg.
      // Persist the ABSOLUTE URL so the customer embed (running on
      // stayplay.integolf.com, a different origin from the API) can
      // fetch the image. Mirror of the pattern in BrandingSetup's
      // featured-image upload. Without this, the embed renders a
      // background-image pointing at its own origin and the image
      // silently 404s into a blank card.
      const apiOrigin = import.meta.env.VITE_API_ORIGIN || window.location.origin
      const fullUrl = data.url.startsWith('http') ? data.url : apiOrigin + data.url
      await tournamentApi.put(`/portal/reseller/packages/${packageId}`, { heroImageUrl: fullUrl })
      setPackages(prev => prev.map(p => p.ID_NO === packageId ? { ...p, HeroImageUrl: fullUrl } : p))
    } catch (e) {
      alert(e?.response?.data?.message || e.message || t('sp_upload_failed', 'Upload failed'))
    }
  }
  async function clearPackageImage(packageId) {
    try {
      await tournamentApi.put(`/portal/reseller/packages/${packageId}`, { heroImageUrl: null })
      setPackages(prev => prev.map(p => p.ID_NO === packageId ? { ...p, HeroImageUrl: '' } : p))
    } catch (e) { alert(e?.response?.data?.message || e.message) }
  }

  async function runExtract() {
    if (!extractUrl.trim()) return
    setExtracting(true); setExtractErr(null); setExtractResult(null)
    try {
      const { data } = await tournamentApi.post('/course-website/extract-styles', { url: extractUrl.trim() })
      setExtractResult(data)
      // Apply extracted values + remember them on the row so the user can
      // revert to the AI's picks later if they manually edit.
      setB(prev => ({
        ...prev,
        primaryColor:           data.primaryColor    || prev.primaryColor,
        accentColor:            data.accentColor     || prev.accentColor,
        secondaryColor:         data.secondaryColor  || prev.secondaryColor,
        headerFont:             data.headerFont      || prev.headerFont,
        bodyFont:               data.bodyFont        || prev.bodyFont,
        sourceUrl:              extractUrl.trim(),
        extractedPrimaryColor:  data.primaryColor    || '',
        extractedAccentColor:   data.accentColor     || '',
        extractedSecondaryColor:data.secondaryColor  || '',
        extractedHeaderFont:    data.headerFont      || '',
        extractedBodyFont:      data.bodyFont        || '',
      }))
    } catch (e) { setExtractErr(e?.response?.data?.message || e.message) }
    finally { setExtracting(false) }
  }

  if (loading || !b) return <div style={empty}>{t('sp_loading_branding', 'Loading branding…')}</div>

  return (
    <>
      {err && <div style={brandErrBox}>{err}</div>}

      {/* AI extract */}
      <Section title={t('sp_match_website_ai', 'Match a website (AI-powered)')} subtitle={t('sp_match_website_subtitle', "Paste a URL and we'll pull the brand colors + fonts.")}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="url"
            value={extractUrl}
            onChange={e => setExtractUrl(e.target.value)}
            placeholder="https://yourbrand.com"
            style={{ ...inp, flex: 1, minWidth: 280 }}
          />
          <button onClick={runExtract} disabled={extracting || !extractUrl.trim()} style={{ ...primaryBtn, opacity: (extracting || !extractUrl.trim()) ? 0.5 : 1 }}>
            {extracting ? t('sp_extracting', 'Extracting…') : t('sp_extract_styles', '✨ Extract styles')}
          </button>
        </div>
        {extractErr && <div style={{ ...brandErrBox, marginTop: 8 }}>{extractErr}</div>}
        {extractResult && (
          <div style={{ marginTop: 10, padding: 10, background: '#f0fdfa', border: '1px solid #a7f3d0', borderRadius: 6, fontSize: 13 }}>
            <strong>{t('sp_pulled_from_url', 'Pulled from {url}:', { url: extractResult.url || extractUrl })}</strong>
            <div style={{ display: 'flex', gap: 14, marginTop: 6, flexWrap: 'wrap' }}>
              {extractResult.primaryColor && <ColorPill label={t('sp_primary_label', 'Primary')} hex={extractResult.primaryColor} />}
              {extractResult.accentColor && <ColorPill label={t('sp_accent_label', 'Accent')} hex={extractResult.accentColor} />}
              {extractResult.secondaryColor && <ColorPill label={t('sp_secondary_label', 'Secondary')} hex={extractResult.secondaryColor} />}
              {extractResult.headerFont && <span style={{ fontSize: 12, color: '#0f766e' }}>{t('sp_extract_header', 'Header: {font}', { font: extractResult.headerFont })}</span>}
              {extractResult.bodyFont && <span style={{ fontSize: 12, color: '#0f766e' }}>{t('sp_extract_body', 'Body: {font}', { font: extractResult.bodyFont })}</span>}
            </div>
          </div>
        )}
      </Section>

      {/* Logo */}
      <Section title={t('sp_logo', 'Logo')} subtitle={t('sp_logo_subtitle', 'Used in the customer iframe header. Defaults to your account brand profile logo when blank.')}>
        <ImageUploader
          currentUrl={b.logoUrl}
          fallbackUrl={defaults.logoUrl}
          previewMaxHeight={64}
          onChange={(url) => set('logoUrl', url)}
          label={t('sp_logo', 'Logo')}
        />
      </Section>

      {/* Colors */}
      <Section title={t('sp_colors', 'Colors')} subtitle={defaults.primaryColor ? t('sp_colors_inherited', 'Defaults inherited from your brand profile (Primary {primary}{secondary}).', { primary: defaults.primaryColor, secondary: defaults.secondaryColor ? `, Secondary ${defaults.secondaryColor}` : '' }) : t('sp_colors_set_palette', 'Set the customer-facing palette.')}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <ColorField label={t('sp_primary_label', 'Primary')}   value={b.primaryColor}   onChange={v => set('primaryColor', v)}   placeholder={defaults.primaryColor} />
          <ColorField label={t('sp_accent_label', 'Accent')}    value={b.accentColor}    onChange={v => set('accentColor', v)} />
          <ColorField label={t('sp_secondary_label', 'Secondary')} value={b.secondaryColor} onChange={v => set('secondaryColor', v)} placeholder={defaults.secondaryColor} />
        </div>
      </Section>

      {/* Typography preset picker */}
      <Section title={t('sp_typography_style', 'Typography style')} subtitle={t('sp_typography_subtitle', 'Pick a preset to bulk-fill the font, weight, and casing. Choose Custom to fine-tune each value or to keep AI-extracted styles.')}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
          {STYLE_PRESETS.map(p => {
            const active = activePreset === p.v
            return (
              <button
                key={p.v}
                type="button"
                onClick={() => applyStylePreset(p.v)}
                style={{
                  padding: '12px 10px',
                  border: '2px solid ' + (active ? '#00d19f' : '#e2e8f0'),
                  borderRadius: 8,
                  background: active ? '#f0fdfa' : '#fff',
                  cursor: 'pointer',
                  textAlign: 'center',
                  fontFamily: p.sample?.headerFont ? `'${p.sample.headerFont}', serif` : 'inherit',
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 14, color: active ? '#0f766e' : '#0f172a', marginBottom: 4 }}>{t(STYLE_PRESET_NAME_KEYS[p.v], p.name)}</div>
                <div style={{ fontSize: 11, color: '#64748b', fontFamily: 'inherit' }}>{t(STYLE_PRESET_DESC_KEYS[p.v], p.desc)}</div>
              </button>
            )
          })}
        </div>

        {/* Custom font fine-tuning — visible always so admin can override after preset pick */}
        <div style={{ marginTop: 16, padding: 14, background: '#f8fafc', borderRadius: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
            {t('sp_fine_tune', 'Fine-tune')} {activePreset !== 'custom' && <span style={{ fontWeight: 400, textTransform: 'none' }}>{t('sp_switches_to_custom', '(switches to Custom)')}</span>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label={t('sp_header_font', 'Header font')}>
              <select value={b.headerFont || ''} onChange={e => set('headerFont', e.target.value)} style={inp}>
                {FONT_OPTIONS.map(o => <option key={o.label} value={o.v}>{o.v === '' ? t('sp_font_system_default', o.label) : o.label}</option>)}
              </select>
            </Field>
            <Field label={t('sp_body_font', 'Body font')}>
              <select value={b.bodyFont || ''} onChange={e => set('bodyFont', e.target.value)} style={inp}>
                {FONT_OPTIONS.map(o => <option key={o.label} value={o.v}>{o.v === '' ? t('sp_font_system_default', o.label) : o.label}</option>)}
              </select>
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Field label={t('sp_header_weight', 'Header weight')}>
              <select value={b.headerWeight ?? 700} onChange={e => set('headerWeight', Number(e.target.value))} style={inp}>
                {[300,400,500,600,700,800,900].map(w => <option key={w} value={w}>{w}</option>)}
              </select>
            </Field>
            <Field label={t('sp_header_case', 'Header case')}>
              <select value={b.headerCase || 'normal'} onChange={e => set('headerCase', e.target.value)} style={inp}>
                <option value="normal">{t('sp_grouping_case_normal', 'Normal')}</option>
                <option value="uppercase">{t('sp_case_uppercase', 'UPPERCASE')}</option>
                <option value="lowercase">{t('sp_case_lowercase', 'lowercase')}</option>
                <option value="capitalize">{t('sp_case_capitalize', 'Capitalize')}</option>
              </select>
            </Field>
            <Field label={t('sp_header_letter_spacing', 'Header letter spacing')}>
              <select value={b.headerLetterSpacing || 'normal'} onChange={e => set('headerLetterSpacing', e.target.value)} style={inp}>
                <option value="normal">{t('sp_grouping_spacing_normal', 'Normal')}</option>
                <option value="0.04em">{t('sp_spacing_wide', 'Wide')}</option>
                <option value="0.15em">{t('sp_spacing_wider', 'Wider')}</option>
                <option value="-0.02em">{t('sp_spacing_tight', 'Tight')}</option>
              </select>
            </Field>
          </div>
        </div>
      </Section>

      {/* Layout — S&P gets all 10 tiles; bundle groupings get the focused
          subset (cards / comparison / minimal / featured). */}
      <Section title={t('sp_layout_h', 'Layout')} subtitle={isBundles
        ? t('sp_layout_subtitle_bundles', 'How your bundles display in the customer embed. Each layout has its own template.')
        : t('sp_layout_subtitle', 'How packages display in the customer iframe. Each layout has its own template.')}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8 }}>
          {(isBundles ? LAYOUT_OPTIONS.filter(o => BUNDLE_LAYOUTS.includes(o.v)) : LAYOUT_OPTIONS).map(opt => {
            const active = (b.layout || 'cards') === opt.v
            return (
              <button
                key={opt.v}
                type="button"
                onClick={() => set('layout', opt.v)}
                style={{
                  padding: '14px 10px',
                  border: '2px solid ' + (active ? '#00d19f' : '#e2e8f0'),
                  borderRadius: 8,
                  background: active ? '#f0fdfa' : '#fff',
                  cursor: 'pointer',
                  textAlign: 'center',
                  fontFamily: 'inherit',
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 14, color: active ? '#0f766e' : '#0f172a', marginBottom: 4 }}>{t(LAYOUT_NAME_KEYS[opt.v], opt.name)}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>{t(LAYOUT_DESC_KEYS[opt.v], opt.desc)}</div>
              </button>
            )
          })}
        </div>
        {/* Featured layout = ONE shared image at the grouping level */}
        {b.layout === 'featured' && (
          <div style={{ marginTop: 16, padding: 16, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>{t('sp_featured_image', 'Featured image')}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
              {t('sp_featured_image_help', 'Upload a hero image for your featured package. Max 8MB. JPG, PNG, WebP, or GIF.')}
            </div>
            <ImageUploader
              currentUrl={b.heroImageUrl}
              previewMaxHeight={180}
              onChange={(url) => set('heroImageUrl', url)}
              label={t('sp_featured_image', 'Featured image')}
            />
          </div>
        )}

        {/* Hero / Gallery / Carousel / Split = PER-PACKAGE image. Each
            package gets its own slot; the URL is saved directly to
            reseller_packages.HeroImageUrl so the customer iframe can
            read it independent of the grouping branding row. */}
        {['hero', 'gallery', 'carousel', 'split'].includes(b.layout || 'cards') && (
          <div style={{ marginTop: 16, padding: 16, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>{t('sp_package_images', 'Package images')}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
              {t('sp_package_images_help', 'Upload an image for each package. These show as the hero image in the {layout} layout.', { layout: b.layout })}
            </div>
            {packages.length === 0 ? (
              <div style={{ padding: 12, color: '#94a3b8', fontSize: 13, textAlign: 'center' }}>
                {t('sp_no_packages_create_first', 'No packages yet — create some on the Packages tab first.')}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {packages.map(p => (
                  <PackageImageRow
                    key={p.ID_NO}
                    pkg={p}
                    onUpload={(file) => uploadPackageImage(p.ID_NO, file)}
                    onClear={() => clearPackageImage(p.ID_NO)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </Section>

      {/* Live preview — shared component with the regular Stay & Play
          BrandingSetup so all 10 layout templates render identically.
          Bundle groupings pass mode="bundles" + their real bundles so the
          preview shows prepaid-pass cards instead of S&P package cards. */}
      <Section title={t('preview', 'Preview')} subtitle={isBundles
        ? t('sp_preview_subtitle_bundles', 'Your bundles rendered as the golfer will see them, with your fonts and colors applied. Primary drives titles; accent drives prices and CTAs.')
        : t('sp_preview_subtitle', 'All your active packages rendered in the selected layout, with your fonts and colors applied. Primary drives titles and brand anchors; accent drives prices and CTAs.')}>
        <PreviewWidget
          mode={isBundles ? 'bundles' : undefined}
          packages={isBundles
            ? bundles.map(b => ({ ...b, Description: (langId === 2 && b.DescriptionFr) ? b.DescriptionFr : b.Description }))
            : packages}
          branding={{
            logoUrl:                b.logoUrl || defaults.logoUrl || '',
            primaryColor:           b.primaryColor || defaults.primaryColor || '',
            accentColor:            b.accentColor || '',
            secondaryColor:         b.secondaryColor || defaults.secondaryColor || '',
            headerFont:             b.headerFont || '',
            bodyFont:               b.bodyFont || '',
            headerWeight:           b.headerWeight ?? 700,
            headerCase:             b.headerCase || 'normal',
            headerLetterSpacing:    b.headerLetterSpacing || 'normal',
            layout:                 b.layout || 'cards',
            featuredImageUrl:       b.heroImageUrl || '',
            // packageImages = optional per-package override map. We keep
            // it empty so the renderer falls back to pkg.HeroImageUrl,
            // which already lives on each reseller_packages row.
            packageImages:          {},
          }}
        />
      </Section>

      {/* Save bar */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, marginTop: 16, paddingTop: 14, borderTop: '1px solid #e2e8f0' }}>
        {savedAt && <span style={{ color: '#166534', fontSize: 13, fontWeight: 600 }}>{t('sp_saved_at_check', '✓ Saved at {time}', { time: savedAt.toLocaleTimeString() })}</span>}
        <button onClick={save} disabled={saving} style={{ ...primaryBtn, opacity: saving ? 0.5 : 1 }}>
          {saving ? t('sp_saving', 'Saving…') : t('sp_save_branding', 'Save branding')}
        </button>
      </div>
    </>
  )
}

/**
 * ImageUploader — file-picker UX matching the regular S&P branding setup.
 * Posts to /portal/reseller/uploads/image (multer-backed). Returns a local
 * URL we store in the branding state. Shows a preview of the current image
 * (uploaded or inherited fallback) with a Replace button.
 *
 * No URL input — Todd's spec: file uploads only. Server defensively
 * downloads any remote URL on save anyway, so we never link out.
 */
function ImageUploader({ currentUrl, fallbackUrl, previewMaxHeight = 96, onChange, label }) {
  const { t } = useLanguage()
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)
  const display = currentUrl || fallbackUrl
  const isFallback = !currentUrl && !!fallbackUrl

  // Resolve a relative /site-assets/ URL to absolute so it loads correctly
  // even when the customer iframe is hosted off a third-party domain.
  function resolveSrc(u) {
    if (!u) return null
    if (u.startsWith('http')) return u
    const apiOrigin = import.meta.env.VITE_API_ORIGIN || window.location.origin
    return apiOrigin + u
  }

  async function handleFile(file) {
    if (!file) return
    setUploading(true); setError(null)
    try {
      const fd = new FormData()
      fd.append('image', file)
      const { data } = await tournamentApi.post('/portal/reseller/uploads/image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      onChange(data.url)
    } catch (e) {
      setError(e?.response?.data?.message || e.message || t('sp_upload_failed', 'Upload failed'))
    } finally { setUploading(false) }
  }

  return (
    <div>
      {display && (
        <div style={{
          marginBottom: 10, padding: 10, background: '#f8fafc',
          borderRadius: 6, display: 'inline-block', maxWidth: '100%',
        }}>
          <img
            src={resolveSrc(display)}
            alt={label}
            style={{ maxHeight: previewMaxHeight, maxWidth: 360, objectFit: 'contain', display: 'block' }}
          />
          {isFallback && (
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
              {t('sp_inherited_brand_override', 'Inherited from your account brand profile — upload a new one to override for this grouping.')}
            </div>
          )}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{
          padding: '8px 14px', background: '#fff', color: '#475569',
          border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13,
          cursor: uploading ? 'wait' : 'pointer', display: 'inline-block',
        }}>
          {uploading ? t('sp_uploading', 'Uploading…') : (currentUrl ? t('sp_replace_label', 'Replace {label}', { label: label.toLowerCase() }) : t('sp_upload_label', 'Upload {label}', { label: label.toLowerCase() }))}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            disabled={uploading}
            onChange={(e) => handleFile(e.target.files?.[0])}
            style={{ display: 'none' }}
          />
        </label>
        {currentUrl && (
          <button
            type="button"
            onClick={() => onChange('')}
            style={{ padding: '8px 12px', background: '#fff', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}
          >
            {t('sp_remove', 'Remove')}
          </button>
        )}
        <span style={{ fontSize: 11, color: '#94a3b8' }}>{t('sp_image_size_help', 'JPG, PNG, WEBP, or GIF — up to 8MB')}</span>
      </div>
      {error && <div style={{ marginTop: 8, fontSize: 13, color: '#991b1b' }}>{error}</div>}
    </div>
  )
}

function ColorField({ label, value, onChange, placeholder }) {
  const display = value || ''
  return (
    <div>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>{label}</label>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input
          type="color"
          value={/^#[0-9a-f]{6}$/i.test(display) ? display : '#000000'}
          onChange={e => onChange(e.target.value)}
          style={{ width: 36, height: 36, padding: 2, border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', background: '#fff' }}
        />
        <input
          type="text"
          value={display}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder || '#00d19f'}
          style={{ ...inp, flex: 1, fontFamily: 'monospace' }}
        />
      </div>
    </div>
  )
}

function ColorPill({ label, hex }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#0f766e' }}>
      <span style={{ width: 14, height: 14, borderRadius: 3, background: hex, border: '1px solid #94a3b8' }} />
      {label} <code>{hex}</code>
    </span>
  )
}

/**
 * PackageImageRow — single line in the per-package image upload list shown
 * for hero/gallery/carousel/split layouts. Mirrors S&P's package image row.
 */
function PackageImageRow({ pkg, onUpload, onClear }) {
  const { t } = useLanguage()
  const [busy, setBusy] = useState(false)
  function resolveSrc(u) {
    if (!u) return null
    if (u.startsWith('http')) return u
    const apiOrigin = import.meta.env.VITE_API_ORIGIN || window.location.origin
    return apiOrigin + u
  }
  async function handle(file) {
    if (!file) return
    setBusy(true)
    try { await onUpload(file) } finally { setBusy(false) }
  }
  return (
    <div style={{
      display: 'flex', gap: 12, alignItems: 'center',
      padding: 10, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
    }}>
      {pkg.HeroImageUrl ? (
        <div style={{
          width: 120, height: 80, flexShrink: 0, borderRadius: 6,
          background: `url('${resolveSrc(pkg.HeroImageUrl)}') center/cover no-repeat`,
          border: '1px solid #e2e8f0',
        }} />
      ) : (
        <div style={{
          width: 120, height: 80, flexShrink: 0, borderRadius: 6,
          background: '#f1f5f9', border: '1px dashed #cbd5e1',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 11, color: '#94a3b8',
        }}>{t('sp_no_image', 'No image')}</div>
      )}
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', marginBottom: 6 }}>{pkg.PackageName}</div>
        <div style={{ display: 'flex', gap: 6 }}>
          <label style={{
            padding: '5px 12px', fontSize: 11, fontWeight: 600,
            background: '#fff', color: '#475569',
            border: '1px solid #e2e8f0', borderRadius: 4,
            cursor: busy ? 'wait' : 'pointer',
          }}>
            {busy ? t('sp_uploading', 'Uploading…') : (pkg.HeroImageUrl ? t('sp_replace', 'Replace') : t('sp_upload', 'Upload'))}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              disabled={busy}
              style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; handle(f) }}
            />
          </label>
          {pkg.HeroImageUrl && (
            <button
              type="button"
              onClick={onClear}
              style={{ padding: '5px 12px', fontSize: 11, fontWeight: 600, background: '#fff', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 4, cursor: 'pointer' }}
            >{t('sp_remove', 'Remove')}</button>
          )}
        </div>
      </div>
    </div>
  )
}

const brandErrBox = { padding: 12, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6, marginBottom: 12, fontSize: 14 }

// ─── Embed tab ──────────────────────────────────────────────────────────

function EmbedTab({ grouping, onChanged }) {
  const { t } = useLanguage()
  const embedUrl = `${window.location.origin}/embed/reseller/${grouping.IframeKey}`
  // Auto-resizing iframe: the widget posts its content height and this tiny
  // listener resizes the iframe to match (scrolling="no" so there's no inner
  // scrollbar). No fixed height needed — it grows/shrinks to fit on any screen.
  // Harmless for existing fixed-height embeds: a host with no listener ignores
  // the message.
  const iframeCode = `<iframe id="integolf-embed" src="${embedUrl}" width="100%" scrolling="no" frameborder="0" allowtransparency="true" style="border:none;max-width:1200px;width:100%;min-height:600px;display:block;margin:0 auto;background:transparent;"></iframe>
<script>
(function(){
  var f=document.getElementById('integolf-embed');
  window.addEventListener('message',function(e){
    if(!f||!e.data||e.data.type!=='integolf-embed-resize')return;
    if(e.source&&f.contentWindow&&e.source!==f.contentWindow)return;
    var h=e.data.height;
    if(typeof h==='number'&&h>0){f.style.height=h+'px';}
  });
})();
</script>`

  const [showHeader, setShowHeader] = useState(grouping.ShowHeader == null ? true : !!grouping.ShowHeader)
  const [saving, setSaving] = useState(false)
  async function toggleHeader(next) {
    setShowHeader(next); setSaving(true)
    try {
      await tournamentApi.put(`/portal/reseller/groupings/${grouping.ID_NO}`, { showHeader: next ? 1 : 0 })
      if (onChanged) onChanged()
    } catch (err) {
      // Revert on failure so the toggle reflects DB truth.
      setShowHeader(!next)
      alert(err?.response?.data?.message || err.message)
    } finally { setSaving(false) }
  }

  return (
    <>
      <Section title={t('sp_iframe_display_options', 'Iframe display options')} subtitle={t('sp_iframe_display_subtitle', 'Applies to the customer iframe for this grouping. Toggle anything here, the embed code stays the same — your site picks up the new look on the next load.')}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
          <Toggle checked={showHeader} onChange={toggleHeader} disabled={saving} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', cursor: 'pointer' }} onClick={() => !saving && toggleHeader(!showHeader)}>
              {t('sp_show_iframe_header', 'Show iframe header (logo + support email)')}
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
              {t('sp_show_iframe_header_help', "Turn off if you're embedding this iframe on a page that already shows your branding. Applies to every package in this grouping.")}
            </div>
          </div>
        </div>
      </Section>

      <Section title={t('sp_grouping_embed_code_h', 'Embed code')} subtitle={t('sp_embed_code_subtitle', 'Drop this iframe on your website to let customers browse + book the packages in this grouping. The iframe auto-resizes to its content — no fixed height needed.')}>
        <div style={{ marginBottom: 12 }}>
          <strong style={{ fontSize: 13, color: '#475569' }}>{t('sp_embed_url_label', 'Embed URL:')}</strong>
          <CodeBlockWithCopy text={embedUrl} />
        </div>
        <div>
          <strong style={{ fontSize: 13, color: '#475569' }}>{t('sp_iframe_code_label', 'Iframe code (paste into your site):')}</strong>
          <CodeBlockWithCopy text={iframeCode} />
        </div>
      </Section>
    </>
  )
}

// Copy-to-clipboard wrapper for embed code blocks. Position the button
// in the top-right of the <pre> so it doesn't collide with the text.
function CodeBlockWithCopy({ text }) {
  const { t } = useLanguage()
  const [copied, setCopied] = useState(false)
  async function copy() {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Fallback for older browsers / non-https contexts
      const ta = document.createElement('textarea')
      ta.value = text; document.body.appendChild(ta); ta.select()
      try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 1500) } catch {}
      document.body.removeChild(ta)
    }
  }
  return (
    <div style={{ position: 'relative' }}>
      <pre style={{ ...code, paddingRight: 80 }}>{text}</pre>
      <button
        onClick={copy}
        style={{
          position: 'absolute', top: 8, right: 8,
          padding: '4px 10px', fontSize: 11, fontWeight: 600,
          background: copied ? '#16a34a' : '#475569',
          color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer',
        }}
      >
        {copied ? t('sp_copied', '✓ Copied') : t('copy', 'Copy')}
      </button>
    </div>
  )
}

// ─── Shared ─────────────────────────────────────────────────────────────

// EPIC #116 — Bundles tab, shown on a bundles-type grouping in place of the
// Packages tab. Lists the group's prepaid-round bundles; rows + "New bundle"
// route to the bundle editor (which is scoped to this grouping).
function BundlesTab({ grouping }) {
  const { t } = useLanguage()
  const navigate = useNavigate()
  const [bundles, setBundles] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  function reload() {
    setLoading(true); setError(null)
    tournamentApi.get('/portal/reseller/bundles', { params: { groupingId: grouping.ID_NO } })
      .then(r => setBundles(r.data?.bundles || []))
      .catch(e => setError(e?.response?.data?.message || e.message))
      .finally(() => setLoading(false))
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload() }, [grouping.ID_NO])

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 16 }}>
        <p style={{ color: '#64748b', maxWidth: 640, margin: 0, fontSize: 14, lineHeight: 1.5 }}>
          {t('sp_bundles_tab_intro', 'Bundles live inside this group. Each is a set number of prepaid rounds for a fixed price, with its own course, time/day restrictions and expiry. Click a bundle to edit it.')}
        </p>
        <button onClick={() => navigate(`/portal/reseller/golf-bundles/new?groupingId=${grouping.ID_NO}`)} style={primaryBtn}>
          {t('sp_bundles_new', '+ New bundle')}
        </button>
      </div>

      {error && <div style={errorBox}>{error}</div>}

      {loading
        ? <div style={empty}>{t('sp_loading_short', 'Loading…')}</div>
        : bundles.length === 0
          ? <div style={empty}>{t('sp_bundles_none', 'No bundles yet. Click "+ New bundle" to create your first.')}</div>
          : (
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead><tr>
                  <th style={th}>{t('sp_name_label', 'Name')}</th>
                  <th style={th}>{t('sp_bundles_col_rounds', 'Rounds')}</th>
                  <th style={th}>{t('sp_bundles_col_price', 'Price')}</th>
                  <th style={th}>{t('sp_bundles_col_courses', 'Courses')}</th>
                  <th style={th}>{t('status', 'Status')}</th>
                </tr></thead>
                <tbody>
                  {bundles.map(b => (
                    <tr key={b.ID_NO} style={{ borderTop: '1px solid #f1f5f9', cursor: 'pointer' }} onClick={() => navigate(`/portal/reseller/golf-bundles/${b.ID_NO}`)}>
                      <td style={td}><strong style={{ color: '#0f172a' }}>{b.Name}</strong></td>
                      <td style={td}>{b.RoundsCount}</td>
                      <td style={td}>${Number(b.Price).toFixed(2)}</td>
                      <td style={td}>{b.CourseCount}</td>
                      <td style={td}><PackageStatusBadge value={b.Status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
    </>
  )
}

function TabBtn({ active, onClick, children }) {
  return <button onClick={onClick} style={{ padding: '8px 16px', background: active ? '#0f172a' : 'transparent', color: active ? '#fff' : '#64748b', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{children}</button>
}
function Section({ title, subtitle, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 20, marginBottom: 16 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 15, color: '#0f172a' }}>{title}</h3>
      {subtitle && <p style={{ margin: '0 0 14px', fontSize: 12, color: '#64748b' }}>{subtitle}</p>}
      <div style={{ marginTop: subtitle ? 4 : 12 }}>{children}</div>
    </div>
  )
}
function Field({ label, hint, children, style }) {
  return (
    <div style={{ marginBottom: 14, ...style }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{hint}</div>}
    </div>
  )
}
function ListTypeBadge({ value }) {
  const { t } = useLanguage()
  const colors = { include: { bg: '#dbeafe', fg: '#1e40af' }, exclude: { bg: '#fee2e2', fg: '#991b1b' }, preferred: { bg: '#dcfce7', fg: '#166534' } }
  const c = colors[value] || colors.include
  return <span style={{ padding: '2px 8px', background: c.bg, color: c.fg, borderRadius: 999, fontSize: 11, fontWeight: 600 }}>{t('sp_list_' + value, value)}</span>
}

const inp = { width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }
const row = { display: 'flex', gap: 12 }
const errorBox = { padding: 12, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6, marginBottom: 16 }
const successBox = { padding: 12, background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0', borderRadius: 6 }
const empty = { padding: 40, textAlign: 'center', color: '#64748b', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }
const tbl = { width: '100%', borderCollapse: 'collapse', fontSize: 14 }
const tr = { borderBottom: '1px solid #f1f5f9' }
const th = { padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 12, letterSpacing: 0.5, textTransform: 'uppercase', background: '#f8fafc' }
const td = { padding: '14px 16px', verticalAlign: 'middle' }
// Compact table cells for the inline tee-time refund-tier editor.
const ttTh = { padding: '4px 8px 4px 0', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 11 }
const ttTd = { padding: '3px 8px 3px 0', verticalAlign: 'middle' }
const tdMono = { ...td, fontFamily: 'monospace', fontSize: 13 }
const selectStyle = { padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, background: '#fff', minWidth: 220 }
const primaryBtn = { padding: '10px 16px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }
const secondaryBtn = { padding: '8px 14px', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, cursor: 'pointer' }
const code = { background: '#0f172a', color: '#e2e8f0', padding: 12, borderRadius: 6, fontSize: 12, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }
